import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, Settings, Users, History, Sliders, MessageSquare, Flame,
  Plus, Trash2, Edit2, X, Check, Save, Eye, Mail, Phone, RefreshCw,
  Building2, User, Download, ChevronDown, Euro, Fuel, AlertTriangle,
  UserX, Receipt, Wrench, Zap, Leaf, TrendingUp, Target, Calendar,
  Award, FileText, ChevronRight, ArrowRight, Loader2, Shield, Key,
  BookOpen, Calculator, HelpCircle, Sparkles, BarChart3, Truck, Gauge, Monitor, TreePine,
  Link, Clock, MousePointerClick, ExternalLink, Copy, Send, UserPlus, Compass
} from "lucide-react";

import ProspeccionPanel from "./ProspeccionPanel";
import ConfigIAActas from "./ConfigIAActas";
import FunnelTab from "./FunnelTab";
import LeadsBloqueados from "./LeadsBloqueados";
import ZonaNav from "./ZonaNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper para obtener headers con token
const getAuthHeaders = () => {
  const token = localStorage.getItem('mobilsafe_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Axios instance con interceptor para añadir token automáticamente
const authAxios = axios.create();
authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('mobilsafe_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor para manejar errores 401 (token expirado)
authAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('mobilsafe_token');
      localStorage.removeItem('mobilsafe_user');
      localStorage.removeItem('mobilsafe_auth');
      toast.error('Sesión expirada. Por favor, inicia sesión de nuevo.');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

const AdminPanel = ({ onClose, config, onConfigUpdate, comerciales: comercialesProp, onLogout, onOpenConfigConstructor, onOpenClientes, modo = 'admin' }) => {
  const [tab, setTab] = useState(modo === 'zona1' ? 'leads' : 'mensajes');

  // Pestañas visibles según el modo (Zona 1 captación vs Administración global)
  const pestañasZona1 = ['leads', 'bloqueados', 'citas', 'historico'];
  const pestañasAdmin = ['mensajes', 'landings', 'config', 'comerciales', 'config-ia-actas', 'prospeccion-keywords', 'prospeccion-horario', 'prospeccion-modelos', 'usuarios', 'analytics', 'docs', 'reflexiones'];
  const pestañasVisibles = modo === 'zona1' ? pestañasZona1 : pestañasAdmin;
  const [configSubTab, setConfigSubTab] = useState('calculo');
  const [docsTab, setDocsTab] = useState('manual');
  const [comerciales, setComerciales] = useState([]);
  const [simulaciones, setSimulaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingComercial, setEditingComercial] = useState(null);
  const [newComercial, setNewComercial] = useState({ nombre: '', email: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedSector, setSelectedSector] = useState('urbano');
  const [editingConfig, setEditingConfig] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [viewingSimulacion, setViewingSimulacion] = useState(null);
  const [loadingSimulacion, setLoadingSimulacion] = useState(false);
  const [editingPropuesta, setEditingPropuesta] = useState(null);
  
  // Mensajes Quick
  const [mensajesQuick, setMensajesQuick] = useState(null);
  const [editingMensajes, setEditingMensajes] = useState(null);
  const [savingMensajes, setSavingMensajes] = useState(false);
  
  // Leads CAES
  const [leadsCAES, setLeadsCAES] = useState([]);
  // Iter52.82 · Contador de la pestaña de intentos cortados por el anti-bots.
  const [totalBloqueados, setTotalBloqueados] = useState(0);
  // IMPL-031 · filtro por origen para la tabla unificada de leads
  const [leadsFiltroOrigen, setLeadsFiltroOrigen] = useState('todos'); // todos|calculadora|caes|analisis_flota
  const [informesCAES, setInformesCAES] = useState([]);
  const [generandoInforme, setGenerandoInforme] = useState(null);
  
  // Usuarios
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({ nombre: '', email: '', password: '', role: 'comercial' });
  const [showAddUserForm, setShowAddUserForm] = useState(false);

  // Reflexiones
  const [expandedReflexion, setExpandedReflexion] = useState(null);
  const reflexiones = [
    {
      id: 1,
      titulo: "Prueba de Fondo Blanco",
      fecha: "20/03/2026",
      descripcion: "Mockup para visualizar cómo quedarían las páginas principales (Landing Simulador y Landing CAES) con fondo blanco en lugar del fondo oscuro actual.",
      link: "/mockup-blanco",
      linkTexto: "Ver mockup de fondo blanco",
      estado: "Pendiente de decisión",
      notas: "Se creó un componente separado (MockupFondoBlanco.jsx) que NO afecta al código real. Es solo para visualización y toma de decisión sobre si cambiar el diseño."
    }
  ];

  // Textos de Landings (CMS)
  const [landingTextos, setLandingTextos] = useState([]);
  const [editingLandingTextos, setEditingLandingTextos] = useState(null);
  const [expandedLanding, setExpandedLanding] = useState(null);
  const [expandedSeccion, setExpandedSeccion] = useState(null);
  const [savingLandingTextos, setSavingLandingTextos] = useState(false);

  // FAQs Configurables
  const [faqsConfig, setFaqsConfig] = useState([]);
  const [editingFaqs, setEditingFaqs] = useState(null);
  const [savingFaqs, setSavingFaqs] = useState(false);
  const [landingsSubTab, setLandingsSubTab] = useState('textos'); // 'textos' o 'faqs'

  // Analytics
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [visitors, setVisitors] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [analyticsTab, setAnalyticsTab] = useState('overview');

  // Citas
  const [citas, setCitas] = useState([]);
  const [configCitas, setConfigCitas] = useState(null);
  const [editingConfigCitas, setEditingConfigCitas] = useState(null);
  const [savingConfigCitas, setSavingConfigCitas] = useState(false);
  const [citasTab, setCitasTab] = useState('lista'); // 'lista', 'calendario', 'config'

  // Datos MITECO
  const [datosMiteco, setDatosMiteco] = useState(null);
  const [savingMiteco, setSavingMiteco] = useState(false);
  const [syncingMiteco, setSyncingMiteco] = useState(false);

  // Obtener usuario actual del localStorage
  const currentUser = JSON.parse(localStorage.getItem('mobilsafe_user') || '{}');
  const isAdmin = ["admin", "super_admin", "superadmin"].includes(currentUser.role);
  // Iter52.72 · Recuperar leads CAES (solo super_admin)
  const isSuperAdmin = ["super_admin", "superadmin"].includes(currentUser.role);
  const [recuperaCAES, setRecuperaCAES] = useState(null);
  const [recuperaCAESCargando, setRecuperaCAESCargando] = useState(false);

  const previsualizarRecuperarCAES = async () => {
    setRecuperaCAESCargando(true);
    try {
      const { data } = await authAxios.get(`${API}/leads/recuperar-caes/preview`);
      setRecuperaCAES(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error en la vista previa');
    } finally {
      setRecuperaCAESCargando(false);
    }
  };

  const aplicarRecuperarCAES = async () => {
    if (!window.confirm(`Se copiarán ${recuperaCAES?.a_copiar || 0} leads CAES a Captación. No se envía ningún email. ¿Continuar?`)) return;
    setRecuperaCAESCargando(true);
    try {
      const { data } = await authAxios.post(`${API}/leads/recuperar-caes/aplicar`);
      toast.success(`Copiados ${data.copiados} leads · ${data.ya_presentes} ya estaban`);
      setRecuperaCAES({ ...data, aplicado: true });
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error aplicando la recuperación');
    } finally {
      setRecuperaCAESCargando(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const requests = [
        authAxios.get(`${API}/comerciales`),
        authAxios.get(`${API}/simulaciones`),
        authAxios.get(`${API}/mensajes-quick`),
        authAxios.get(`${API}/leads`),
        authAxios.get(`${API}/informes-caes`),
        authAxios.get(`${API}/citas`),
        authAxios.get(`${API}/config/citas`),
        axios.get(`${API}/config/miteco`)  // Público, no necesita auth
      ];
      // Iter52.82 · Contador de bloqueados (no debe romper la carga si falla).
      authAxios.get(`${API}/leads-bloqueados`, { params: { limit: 1 } })
        .then(r => setTotalBloqueados(r.data?.total || 0))
        .catch(() => setTotalBloqueados(0));
      
      // Solo cargar usuarios y textos de landings si es admin
      if (isAdmin) {
        requests.push(authAxios.get(`${API}/users`));
        requests.push(authAxios.get(`${API}/landing-textos`));
        requests.push(authAxios.get(`${API}/faqs`));
      }
      
      const responses = await Promise.all(requests);
      setComerciales(responses[0].data);
      setSimulaciones(responses[1].data);
      setMensajesQuick(responses[2].data);
      setLeadsCAES(responses[3].data);
      setInformesCAES(responses[4].data);
      setCitas(responses[5].data);
      setConfigCitas(responses[6].data);
      setDatosMiteco(responses[7].data);
      
      if (isAdmin && responses[8]) {
        setUsers(responses[8].data);
      }
      if (isAdmin && responses[9]) {
        setLandingTextos(responses[9].data);
      }
      if (isAdmin && responses[10]) {
        setFaqsConfig(responses[10].data);
      }
    } catch (error) {
      toast.error('Error cargando datos');
    }
    setLoading(false);
  };

  const loadAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const [statsRes, visitorsRes, sessionsRes] = await Promise.all([
        authAxios.get(`${API}/analytics/stats`),
        authAxios.get(`${API}/analytics/visitors?limit=30`),
        authAxios.get(`${API}/analytics/sessions?limit=50`)
      ]);
      setAnalytics(statsRes.data);
      setVisitors(visitorsRes.data);
      setSessions(sessionsRes.data);
    } catch (error) {
      toast.error('Error cargando analytics');
    }
    setLoadingAnalytics(false);
  };

  // Cargar analytics cuando se selecciona la pestaña
  useEffect(() => {
    if (tab === 'analytics' && !analytics) {
      loadAnalytics();
    }
  }, [tab]);

  const handleUpdateLeadCAES = async (leadId, update) => {
    try {
      // IMPL-031 · ahora apunta a /api/leads (colección unificada)
      await authAxios.put(`${API}/leads/${leadId}`, update);
      loadData();
      toast.success('Lead actualizado');
    } catch (error) {
      toast.error('Error actualizando lead');
    }
  };

  const handleDeleteLeadCAES = async (leadId) => {
    if (!window.confirm('¿Eliminar este lead?')) return;
    try {
      // IMPL-031 · ahora apunta a /api/leads
      await authAxios.delete(`${API}/leads/${leadId}`);
      loadData();
      toast.success('Lead eliminado');
    } catch (error) {
      toast.error('Error eliminando lead');
    }
  };

  // Convertir lead → cliente (Zona 1 → Zona 2). Manual (DEC-004).
  const handleConvertirLead = async (lead) => {
    const nombre = window.prompt(
      `Convertir lead "${lead.empresa}" a cliente.\n\nNombre del contacto principal:`,
      lead.empresa || ''
    );
    if (!nombre || !nombre.trim()) return;
    const cargo = window.prompt('Cargo del contacto (opcional):', '') || '';
    try {
      const r = await authAxios.post(`${API}/clientes/convertir-lead`, {
        lead_id: lead.id,
        contacto_nombre: nombre.trim(),
        contacto_cargo: cargo.trim() || null,
      });
      toast.success(`Cliente creado: ${r.data.empresa}`);
      loadData();
    } catch (error) {
      toast.error('Error convirtiendo lead');
    }
  };

  // Generar informe para un lead CAES
  const handleGenerarInforme = async (leadId) => {
    setGenerandoInforme(leadId);
    try {
      const response = await authAxios.post(`${API}/informes-caes?lead_id=${leadId}`);
      loadData();
      const informeUrl = `${window.location.origin}/informe/${response.data.id}`;
      toast.success(
        <div>
          <p className="font-bold">Informe generado</p>
          <p className="text-sm opacity-80">URL copiada al portapapeles</p>
        </div>
      );
      navigator.clipboard.writeText(informeUrl);
    } catch (error) {
      toast.error('Error generando informe');
    }
    setGenerandoInforme(null);
  };

  // Obtener informe de un lead
  const getInformeForLead = (leadId) => {
    return informesCAES.find(i => i.lead_id === leadId);
  };

  // Copiar URL del informe
  const handleCopyInformeUrl = (informeId) => {
    const url = `${window.location.origin}/informe/${informeId}`;
    navigator.clipboard.writeText(url);
    toast.success('URL copiada al portapapeles');
  };

  const handleSaveMensajesQuick = async () => {
    if (!editingMensajes) return;
    setSavingMensajes(true);
    try {
      const response = await authAxios.put(`${API}/mensajes-quick`, editingMensajes);
      setMensajesQuick(response.data);
      setEditingMensajes(null);
      toast.success('Mensajes guardados');
    } catch (error) {
      toast.error('Error guardando mensajes');
    }
    setSavingMensajes(false);
  };

  const handleAddMensaje = (categoria) => {
    if (!editingMensajes) return;
    setEditingMensajes({
      ...editingMensajes,
      [categoria]: [...editingMensajes[categoria], { headline: '', subtext: '' }]
    });
  };

  const handleRemoveMensaje = (categoria, index) => {
    if (!editingMensajes || editingMensajes[categoria].length <= 1) return;
    setEditingMensajes({
      ...editingMensajes,
      [categoria]: editingMensajes[categoria].filter((_, i) => i !== index)
    });
  };

  const handleUpdateMensaje = (categoria, index, field, value) => {
    if (!editingMensajes) return;
    const newMensajes = [...editingMensajes[categoria]];
    newMensajes[index] = { ...newMensajes[index], [field]: value };
    setEditingMensajes({
      ...editingMensajes,
      [categoria]: newMensajes
    });
  };

  // ============================================
  // FUNCIONES DE USUARIOS
  // ============================================
  
  const handleAddUser = async () => {
    if (!newUser.nombre || !newUser.email || !newUser.password) {
      toast.error('Completa nombre, email y contraseña');
      return;
    }
    try {
      await authAxios.post(`${API}/users`, newUser);
      toast.success('Usuario creado');
      setNewUser({ nombre: '', email: '', password: '', role: 'comercial' });
      setShowAddUserForm(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creando usuario');
    }
  };

  const handleUpdateUser = async (userId, data) => {
    try {
      await authAxios.put(`${API}/users/${userId}`, data);
      toast.success('Usuario actualizado');
      setEditingUser(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error actualizando usuario');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('¿Eliminar este usuario? Esta acción no se puede deshacer.')) return;
    try {
      await authAxios.delete(`${API}/users/${userId}`);
      toast.success('Usuario eliminado');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error eliminando usuario');
    }
  };

  // ============================================
  // FUNCIONES DE TEXTOS DE LANDINGS
  // ============================================

  const handleEditLandingTextos = (landing) => {
    // Crear una copia profunda para edición
    setEditingLandingTextos(JSON.parse(JSON.stringify(landing)));
    setExpandedLanding(landing.id);
  };

  const handleCancelEditLanding = () => {
    setEditingLandingTextos(null);
    setExpandedSeccion(null);
  };

  const handleUpdateLandingCampo = (seccionId, campoKey, value) => {
    if (!editingLandingTextos) return;
    const newSecciones = editingLandingTextos.secciones.map(seccion => {
      if (seccion.id === seccionId) {
        return {
          ...seccion,
          campos: { ...seccion.campos, [campoKey]: value }
        };
      }
      return seccion;
    });
    setEditingLandingTextos({ ...editingLandingTextos, secciones: newSecciones });
  };

  const handleSaveLandingTextos = async () => {
    if (!editingLandingTextos) return;
    setSavingLandingTextos(true);
    try {
      await authAxios.put(`${API}/landing-textos/${editingLandingTextos.id}`, {
        secciones: editingLandingTextos.secciones
      });
      toast.success('Textos guardados correctamente');
      setEditingLandingTextos(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error guardando textos');
    }
    setSavingLandingTextos(false);
  };

  const handleResetLandingTextos = async (landingId) => {
    if (!window.confirm('¿Restaurar los textos de esta landing a sus valores originales? Esta acción no se puede deshacer.')) return;
    try {
      await authAxios.post(`${API}/landing-textos/${landingId}/reset`);
      toast.success('Textos restaurados');
      setEditingLandingTextos(null);
      loadData();
    } catch (error) {
      toast.error('Error restaurando textos');
    }
  };

  // ============================================
  // FAQs CONFIGURABLES
  // ============================================
  
  const handleEditFaq = (faqConfig) => {
    setEditingFaqs({
      ...faqConfig,
      faqs: [...faqConfig.faqs].sort((a, b) => a.orden - b.orden)
    });
  };

  const handleUpdateFaqField = (faqId, field, value) => {
    setEditingFaqs(prev => ({
      ...prev,
      faqs: prev.faqs.map(f => f.id === faqId ? { ...f, [field]: value } : f)
    }));
  };

  const handleAddFaq = () => {
    const maxOrden = Math.max(...editingFaqs.faqs.map(f => f.orden), 0);
    setEditingFaqs(prev => ({
      ...prev,
      faqs: [...prev.faqs, {
        id: `new-${Date.now()}`,
        pregunta: '',
        respuesta: '',
        orden: maxOrden + 1,
        activo: true
      }]
    }));
  };

  const handleDeleteFaq = (faqId) => {
    setEditingFaqs(prev => ({
      ...prev,
      faqs: prev.faqs.filter(f => f.id !== faqId)
    }));
  };

  const handleMoveFaq = (faqId, direction) => {
    setEditingFaqs(prev => {
      const faqs = [...prev.faqs].sort((a, b) => a.orden - b.orden);
      const index = faqs.findIndex(f => f.id === faqId);
      if (index === -1) return prev;
      
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= faqs.length) return prev;
      
      // Intercambiar órdenes
      const tempOrden = faqs[index].orden;
      faqs[index].orden = faqs[newIndex].orden;
      faqs[newIndex].orden = tempOrden;
      
      return { ...prev, faqs };
    });
  };

  const handleSaveFaqs = async () => {
    if (!editingFaqs) return;
    setSavingFaqs(true);
    try {
      const faqsToSave = editingFaqs.faqs.map(f => ({
        pregunta: f.pregunta,
        respuesta: f.respuesta,
        orden: f.orden,
        activo: f.activo
      }));
      await authAxios.put(`${API}/faqs/${editingFaqs.pagina}`, faqsToSave);
      toast.success('FAQs guardadas');
      loadData();
    } catch (error) {
      toast.error('Error guardando FAQs');
    }
    setSavingFaqs(false);
  };

  const handleResetFaqs = async (pagina) => {
    if (!window.confirm('¿Restaurar las FAQs a sus valores originales?')) return;
    try {
      await authAxios.post(`${API}/faqs/${pagina}/reset`);
      toast.success('FAQs restauradas');
      setEditingFaqs(null);
      loadData();
    } catch (error) {
      toast.error('Error restaurando FAQs');
    }
  };

  // ============================================

  const handleAddComercial = async () => {
    if (!newComercial.nombre || !newComercial.email) { toast.error('Completa nombre y email'); return; }
    try {
      await authAxios.post(`${API}/comerciales`, newComercial);
      toast.success('Comercial añadido');
      setNewComercial({ nombre: '', email: '' });
      setShowAddForm(false);
      loadData();
    } catch (error) { toast.error('Error añadiendo comercial'); }
  };

  const handleUpdateComercial = async (id, data) => {
    try {
      await authAxios.put(`${API}/comerciales/${id}`, data);
      toast.success('Comercial actualizado');
      setEditingComercial(null);
      loadData();
    } catch (error) { toast.error('Error actualizando'); }
  };

  const handleDeleteComercial = async (id) => {
    if (!window.confirm('¿Eliminar este comercial?')) return;
    try {
      await authAxios.delete(`${API}/comerciales/${id}`);
      toast.success('Comercial eliminado');
      loadData();
    } catch (error) { toast.error('Error eliminando'); }
  };

  const handleDeleteSimulacion = async (id) => {
    if (!window.confirm('¿Eliminar esta simulación?')) return;
    try {
      await authAxios.delete(`${API}/simulaciones/${id}`);
      toast.success('Simulación eliminada');
      loadData();
    } catch (error) { toast.error('Error eliminando'); }
  };

  const handleViewSimulacion = async (id) => {
    setLoadingSimulacion(true);
    try {
      const response = await authAxios.get(`${API}/simulaciones/${id}`);
      setViewingSimulacion(response.data);
    } catch (error) { 
      toast.error('Error cargando simulación'); 
    }
    setLoadingSimulacion(false);
  };

  const handleOpenPropuesta = async (id) => {
    setLoadingSimulacion(true);
    try {
      const response = await authAxios.get(`${API}/simulaciones/${id}`);
      setViewingSimulacion(response.data);
      setEditingPropuesta(id);
    } catch (error) { 
      toast.error('Error cargando simulación'); 
    }
    setLoadingSimulacion(false);
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      await authAxios.put(`${API}/config`, {
        constants: editingConfig.constants,
        benchmarks: editingConfig.benchmarks,
        presets: editingConfig.presets,
        benchmarkSources: editingConfig.benchmarkSources,
        piloto: editingConfig.piloto,
        precios: editingConfig.precios,
        updatedBy: "admin"
      });
      toast.success('Configuración guardada');
      onConfigUpdate(editingConfig);
    } catch (error) { toast.error('Error guardando configuración'); }
    setSavingConfig(false);
  };

  useEffect(() => {
    if (config && !editingConfig) {
      setEditingConfig(JSON.parse(JSON.stringify(config)));
    }
  }, [config]);

  const sectorNames = {
    urbano: '🚌 Urbano', interurbano: '🚍 Interurbano', distribucion: '📦 Distribución',
    logistica: '🚚 Logística', taxi: '🚕 Taxi/VTC'
  };

  const estadoLabels = {
    simulacion_realizada: { label: 'Simulación', color: 'bg-gray-500' },
    propuesta_enviada: { label: 'Propuesta', color: 'bg-blue-500' },
    en_negociacion: { label: 'Negociando', color: 'bg-yellow-500' },
    piloto_aprobado: { label: 'Piloto OK', color: 'bg-emerald-500' },
    piloto_activo: { label: 'Piloto Activo', color: 'bg-green-500' },
    oferta_flota: { label: 'Oferta Flota', color: 'bg-purple-500' },
    ampliacion: { label: 'Ampliación', color: 'bg-indigo-500' },
    despliegue: { label: 'Despliegue', color: 'bg-cyan-500' },
    implantado_100: { label: '100% Implantado', color: 'bg-emerald-600' },
    descartado: { label: 'Descartado', color: 'bg-red-500' }
  };

  const kpiLabels = {
    consumo: '⛽ Consumo (L/100km)', accidentes: '🚨 Accidentes (%)',
    absentismo: '🏥 Absentismo (%)', multas: '🚦 Multas (n/año)', mantenimiento: '🔧 Mantenimiento (€)'
  };

  const constantLabels = {
    PRECIO_MWH: 'Precio MWh (€)', KWH_POR_LITRO: 'kWh por Litro',
    KG_CO2_POR_LITRO: 'kg CO2 por Litro', COSTE_HORA_CONDUCTOR: 'Coste Hora Conductor (€)',
    HORAS_TRABAJO_ANUALES: 'Horas Trabajo Anuales', PORCENTAJE_COMISION_CAES: 'Comisión CAES (%)',
    CIRCUNFERENCIA_TIERRA: 'Circunferencia Tierra (km)'
  };

  const pilotoLabels = {
    vehiculos_piloto: 'Vehículos Piloto',
    duracion_meses_normal: 'Duración Normal (meses)',
    duracion_meses_caes: 'Duración CAES (meses)',
    precio_piloto_normal: 'Precio Piloto Normal (€)',
    precio_caes_entrada: 'Precio CAES Entrada (€)',
    porcentaje_exito_caes_parcial: '% Éxito CAES Parcial',
    porcentaje_exito_caes_total: '% Éxito CAES Total',
    km_minimo_anual: 'Km Mínimo Anual',
    energia_minima_kwh: 'Energía Mínima (kWh)'
  };

  const preciosLabels = {
    // Nombres de productos
    nombre_dispositivo_principal: 'Nombre Dispositivo Principal',
    nombre_iox_auxiliar: 'Nombre IOX/Auxiliar',
    nombre_instalacion: 'Nombre Instalación',
    nombre_saas_basico: 'Nombre SaaS Básico',
    nombre_saas_tacografo: 'Nombre SaaS + Tacógrafo',
    nombre_saas_premium: 'Nombre SaaS Premium',
    // Precios
    precio_dispositivo_go9: 'Precio Dispositivo (€)',
    precio_iox_auxiliar: 'Precio IOX/Auxiliar (€)',
    precio_instalacion: 'Precio Instalación (€)',
    saas_basico: 'Precio SaaS Básico (€/mes)',
    saas_tacografo: 'Precio SaaS + Tacógrafo (€/mes)',
    saas_premium: 'Precio SaaS Premium (€/mes)',
    permanencia_minima_meses: 'Permanencia Mínima (meses)',
    porcentaje_pago_hw_pedido: '% Pago HW al Pedido'
  };

  // IMPL-068 · Colores del header por zona (naranja captación, navy admin)
  const headerBg = modo === 'zona1' ? 'bg-[#f97316]' : 'bg-slate-900';
  const zonaNavKey = modo === 'zona1' ? 'captacion' : 'admin';

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <header className={`${headerBg} px-6 py-3 flex items-center justify-between gap-4`}>
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-sm font-bold text-white truncate">
            {modo === 'zona1' ? 'Captación' : 'Administración'}
          </span>
          <span className="text-xs text-white/60 hidden md:inline">MOBILSAFE</span>
        </div>
        <ZonaNav activa={zonaNavKey} onLogout={onLogout} esAdmin={true} />
      </header>

      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-center gap-x-1 gap-y-1 overflow-x-auto">
            {/* === GRUPO ZONA 1 · CAPTACIÓN === */}
            {modo === 'zona1' && (
              <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold pr-2 self-center">Captación</span>
            )}
            {pestañasVisibles.includes('leads') && (
              <TabBtn active={tab === 'leads'} onClick={() => setTab('leads')} testid="tab-leads">
                Leads <Badge>{leadsCAES.length}</Badge>
              </TabBtn>
            )}
            {pestañasVisibles.includes('bloqueados') && (
              <TabBtn active={tab === 'bloqueados'} onClick={() => setTab('bloqueados')} testid="tab-bloqueados">
                Bloqueados por anti-bots <Badge>{totalBloqueados}</Badge>
              </TabBtn>
            )}
            {pestañasVisibles.includes('citas') && (
              <TabBtn active={tab === 'citas'} onClick={() => setTab('citas')} testid="tab-citas">
                Citas <Badge>{citas.filter(c => c.estado === 'pendiente').length}</Badge>
              </TabBtn>
            )}
            {pestañasVisibles.includes('historico') && (
              <TabBtn active={tab === 'historico'} onClick={() => setTab('historico')} testid="tab-historico">
                Histórico
              </TabBtn>
            )}

            {/* === GRUPO 1 · CONFIG CAPTACIÓN WEB === */}
            {modo === 'admin' && (
              <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold pl-4 pr-2 self-center border-l border-slate-200 ml-2">Config Captación web</span>
            )}
            {pestañasVisibles.includes('mensajes') && (
              <TabBtn active={tab === 'mensajes'} onClick={() => setTab('mensajes')} testid="tab-mensajes">Mensajes Quick</TabBtn>
            )}
            {pestañasVisibles.includes('landings') && isAdmin && (
              <TabBtn active={tab === 'landings'} onClick={() => setTab('landings')} testid="tab-landings">Landings</TabBtn>
            )}
            {pestañasVisibles.includes('config') && (
              <TabBtn active={tab === 'config'} onClick={() => setTab('config')} testid="tab-config">Cálculos</TabBtn>
            )}

            {/* === GRUPO 2 · CONFIG DESARROLLO COMERCIAL === */}
            {modo === 'admin' && (
              <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold pl-4 pr-2 self-center border-l border-slate-200 ml-2">Config Desarrollo Comercial</span>
            )}
            {pestañasVisibles.includes('comerciales') && (
              <TabBtn active={tab === 'comerciales'} onClick={() => setTab('comerciales')} testid="tab-comerciales">Comerciales</TabBtn>
            )}
            {modo === 'admin' && onOpenConfigConstructor && (
              <TabBtn shortcut active={false} onClick={onOpenConfigConstructor} testid="tab-shortcut-constructor" title="Abre Constructor · Configuración (página separada)">
                Constructor Config <ExternalLink className="w-3 h-3 opacity-60" />
              </TabBtn>
            )}
            {modo === 'admin' && onOpenConfigConstructor && (
              <TabBtn shortcut active={false} onClick={() => { onOpenConfigConstructor(); setTimeout(() => { window.location.hash = 'cuestionario'; }, 100); }} testid="tab-shortcut-biblioteca" title="Abre la sección Cuestionario consultivo">
                Biblioteca diagnóstico <ExternalLink className="w-3 h-3 opacity-60" />
              </TabBtn>
            )}
            {modo === 'admin' && onOpenConfigConstructor && (
              <TabBtn shortcut active={false} onClick={() => { onOpenConfigConstructor(); setTimeout(() => { window.location.hash = 'catalogo'; }, 100); }} testid="tab-shortcut-catalogo" title="Abre la sección Catálogo de productos">
                Catálogo productos <ExternalLink className="w-3 h-3 opacity-60" />
              </TabBtn>
            )}
            {pestañasVisibles.includes('config-ia-actas') && isAdmin && (
              <TabBtn active={tab === 'config-ia-actas'} onClick={() => setTab('config-ia-actas')} testid="tab-config-ia-actas">
                Config IA Actas
              </TabBtn>
            )}

            {/* === GRUPO 3 · PROSPECCIÓN CONFIG === */}
            {modo === 'admin' && (
              <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold pl-4 pr-2 self-center border-l border-slate-200 ml-2">Prospección config</span>
            )}
            {pestañasVisibles.includes('prospeccion-keywords') && isAdmin && (
              <TabBtn active={tab === 'prospeccion-keywords'} onClick={() => setTab('prospeccion-keywords')} testid="tab-prospeccion-keywords">Keywords y target</TabBtn>
            )}
            {pestañasVisibles.includes('prospeccion-horario') && isAdmin && (
              <TabBtn active={tab === 'prospeccion-horario'} onClick={() => setTab('prospeccion-horario')} testid="tab-prospeccion-horario">Programación y horario</TabBtn>
            )}
            {pestañasVisibles.includes('prospeccion-modelos') && isAdmin && (
              <TabBtn active={tab === 'prospeccion-modelos'} onClick={() => setTab('prospeccion-modelos')} testid="tab-prospeccion-modelos">Configuración modelos IA</TabBtn>
            )}

            {/* === GRUPO 4 · GLOBAL === */}
            {modo === 'admin' && (
              <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold pl-4 pr-2 self-center border-l border-slate-200 ml-2">Global</span>
            )}
            {pestañasVisibles.includes('usuarios') && isAdmin && (
              <TabBtn active={tab === 'usuarios'} onClick={() => setTab('usuarios')} testid="tab-usuarios">
                Usuarios <Badge>{users.length}</Badge>
              </TabBtn>
            )}
            {pestañasVisibles.includes('analytics') && isAdmin && (
              <TabBtn active={tab === 'analytics'} onClick={() => setTab('analytics')} testid="tab-analytics">Analytics</TabBtn>
            )}
            {pestañasVisibles.includes('docs') && (
              <TabBtn active={tab === 'docs'} onClick={() => setTab('docs')} testid="tab-docs">Documentación</TabBtn>
            )}
            {pestañasVisibles.includes('reflexiones') && isAdmin && (
              <TabBtn active={tab === 'reflexiones'} onClick={() => setTab('reflexiones')} testid="tab-reflexiones">Reflexiones</TabBtn>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pb-8">
        {loading ? (
          <div className="flex items-center justify-center py-20"><RefreshCw className="w-8 h-8 text-emerald-400 animate-spin" /></div>
        ) : (
          <>
            {/* TAB: COMERCIALES */}
            {tab === 'comerciales' && (
              <div className="bg-white shadow-sm border border-slate-200 rounded-2xl p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-black flex items-center gap-3"><Users className="w-6 h-6 text-emerald-400" /> Equipo Comercial</h2>
                  <button onClick={() => setShowAddForm(true)} className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg font-semibold flex items-center gap-2 transition-all" data-testid="add-comercial-btn">
                    <Plus className="w-4 h-4" /> Añadir
                  </button>
                </div>

                {showAddForm && (
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 mb-6">
                    <h3 className="font-bold mb-4">Nuevo Comercial</h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      <input type="text" placeholder="Nombre" value={newComercial.nombre} onChange={(e) => setNewComercial({...newComercial, nombre: e.target.value})} className="px-4 py-2 bg-white border border-slate-300 rounded-lg" />
                      <input type="email" placeholder="Email" value={newComercial.email} onChange={(e) => setNewComercial({...newComercial, email: e.target.value})} className="px-4 py-2 bg-white border border-slate-300 rounded-lg" />
                      <div className="flex gap-2">
                        <button onClick={handleAddComercial} className="flex-1 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg font-semibold flex items-center justify-center gap-2"><Check className="w-4 h-4" /> Guardar</button>
                        <button onClick={() => setShowAddForm(false)} className="px-4 py-2 bg-white hover:bg-slate-100 rounded-lg"><X className="w-4 h-4" /></button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  {comerciales.map((com) => (
                    <div key={com.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between">
                      {editingComercial === com.id ? (
                        <div className="flex-1 grid md:grid-cols-3 gap-4">
                          <input type="text" defaultValue={com.nombre} id={`edit-nombre-${com.id}`} className="px-4 py-2 bg-white border border-slate-300 rounded-lg" />
                          <input type="email" defaultValue={com.email} id={`edit-email-${com.id}`} className="px-4 py-2 bg-white border border-slate-300 rounded-lg" />
                          <div className="flex gap-2">
                            <button onClick={() => { const n = document.getElementById(`edit-nombre-${com.id}`).value; const e = document.getElementById(`edit-email-${com.id}`).value; handleUpdateComercial(com.id, { nombre: n, email: e }); }} className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg"><Check className="w-4 h-4" /></button>
                            <button onClick={() => setEditingComercial(null)} className="px-4 py-2 bg-white hover:bg-slate-100 rounded-lg"><X className="w-4 h-4" /></button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center"><User className="w-6 h-6 text-emerald-400" /></div>
                            <div>
                              <p className="font-bold text-lg">{com.nombre}</p>
                              <p className="text-emerald-700 text-sm flex items-center gap-2"><Mail className="w-4 h-4" /> {com.email}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => setEditingComercial(com.id)} className="p-2 hover:bg-emerald-500/20 rounded-lg transition-all"><Edit2 className="w-4 h-4 text-emerald-400" /></button>
                            <button onClick={() => handleDeleteComercial(com.id)} className="p-2 hover:bg-red-500/20 rounded-lg transition-all"><Trash2 className="w-4 h-4 text-red-400" /></button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Iter52.82 · Intentos cortados por el anti-bots */}
            {tab === 'bloqueados' && (
              <LeadsBloqueados authAxios={authAxios} />
            )}

            {/* IMPL-031 · TAB: LEADS unificados (calculadora/caes/analisis_flota) */}
            {tab === 'leads' && (
              <div className="bg-white shadow-sm border border-orange-500/20 rounded-2xl p-6">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h2 className="text-2xl font-black flex items-center gap-3"><Flame className="w-6 h-6 text-orange-400" /> Leads</h2>
                    <p className="text-orange-700/70 text-sm mt-1">Leads capturados desde calculadora, CAES y análisis de flota</p>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    {isSuperAdmin && (
                      <button
                        onClick={previsualizarRecuperarCAES}
                        disabled={recuperaCAESCargando}
                        data-testid="recuperar-leads-caes-btn"
                        className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold transition-colors disabled:opacity-50 flex items-center gap-2"
                      >
                        {recuperaCAESCargando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                        Recuperar leads CAES
                      </button>
                    )}
                    <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full">{leadsCAES.filter(l => l.estado === 'nuevo').length} nuevos</span>
                    <span className="px-3 py-1 bg-orange-500/20 text-orange-700 rounded-full">{leadsCAES.length} total</span>
                  </div>
                </div>

                {/* Iter52.72 · Vista previa / aplicar la recuperación de leads CAES */}
                {isSuperAdmin && recuperaCAES && (
                  <div className="mb-5 rounded-xl border border-blue-500/30 bg-blue-50/60 p-4" data-testid="recuperar-leads-caes-panel">
                    <div className="flex items-start justify-between gap-4">
                      <div className="text-sm text-slate-700">
                        <p className="font-bold mb-1">
                          {recuperaCAES.aplicado ? 'Recuperación aplicada' : 'Vista previa (no se ha escrito nada)'}
                        </p>
                        <p data-testid="recuperar-leads-caes-recuento">
                          Confirmados desde {String(recuperaCAES.desde).slice(0, 10)}: <b>{recuperaCAES.confirmados}</b> ·
                          ya en Captación: <b>{recuperaCAES.ya_presentes}</b> ·
                          {recuperaCAES.aplicado ? ' copiados: ' : ' se copiarían: '}
                          <b>{recuperaCAES.aplicado ? recuperaCAES.copiados : recuperaCAES.a_copiar}</b>
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {!recuperaCAES.aplicado && recuperaCAES.a_copiar > 0 && (
                          <button
                            onClick={aplicarRecuperarCAES}
                            disabled={recuperaCAESCargando}
                            data-testid="recuperar-leads-caes-aplicar"
                            className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors disabled:opacity-50"
                          >
                            Aplicar
                          </button>
                        )}
                        <button
                          onClick={() => setRecuperaCAES(null)}
                          data-testid="recuperar-leads-caes-cerrar"
                          className="px-3 py-1.5 rounded-lg bg-white hover:bg-slate-100 text-slate-600 text-xs font-semibold transition-colors"
                        >
                          Cerrar
                        </button>
                      </div>
                    </div>
                    {(recuperaCAES.leads || []).length > 0 && (
                      <ul className="mt-3 space-y-1 text-xs text-slate-600" data-testid="recuperar-leads-caes-lista">
                        {recuperaCAES.leads.map((l) => (
                          <li key={l.id} className="flex flex-wrap gap-x-2">
                            <span className="font-semibold">{l.empresa || '(sin empresa)'}</span>
                            <span>· {l.email}</span>
                            <span>· {String(l.fecha).slice(0, 10)}</span>
                            <span>· {l.estado}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {/* Filtro por origen */}
                <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="leads-filtro-origen">
                  <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Origen:</span>
                  {[
                    { v: 'todos', label: 'Todos' },
                    { v: 'calculadora', label: 'Calculadora', color: 'bg-emerald-500/20 text-emerald-700' },
                    { v: 'caes', label: 'CAES', color: 'bg-blue-500/20 text-blue-700' },
                    { v: 'analisis_flota', label: 'Análisis flota', color: 'bg-orange-500/20 text-orange-700' },
                  ].map((f) => {
                    const active = leadsFiltroOrigen === f.v;
                    return (
                      <button
                        key={f.v}
                        onClick={() => setLeadsFiltroOrigen(f.v)}
                        data-testid={`leads-filtro-${f.v}`}
                        className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${active ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                      >
                        {f.label}
                      </button>
                    );
                  })}
                </div>

                {(() => {
                  const filtered = leadsFiltroOrigen === 'todos'
                    ? leadsCAES
                    : leadsCAES.filter(l => l.origen === leadsFiltroOrigen);
                  if (filtered.length === 0) {
                    return (
                      <div className="text-center py-12 text-orange-700/70">
                        <Flame className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No hay leads con este filtro.</p>
                      </div>
                    );
                  }
                  return (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs uppercase text-orange-700/80 border-b border-orange-500/20">
                            <th className="py-3 px-3">Fecha</th>
                            <th className="py-3 px-3">Origen</th>
                            <th className="py-3 px-3">CTA</th>
                            <th className="py-3 px-3">Contacto</th>
                            <th className="py-3 px-3">Empresa</th>
                            <th className="py-3 px-3">Ahorro</th>
                            <th className="py-3 px-3">Estado</th>
                            <th className="py-3 px-3">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filtered.map((lead) => {
                            const origenColors = {
                              calculadora: 'bg-emerald-500/20 text-emerald-700',
                              caes: 'bg-blue-500/20 text-blue-700',
                              analisis_flota: 'bg-orange-500/20 text-orange-700',
                            };
                            const fechaStr = lead.created_at || lead.createdAt;
                            const ahorro = lead.datos?.ahorro_total;
                            return (
                              <tr key={lead.id} className="border-b border-orange-500/10 hover:bg-orange-500/5" data-testid={`lead-row-${lead.id}`}>
                                <td className="py-3 px-3 text-orange-700">
                                  {fechaStr ? new Date(fechaStr).toLocaleDateString('es-ES') : '-'}
                                </td>
                                <td className="py-3 px-3">
                                  <span className={`px-2 py-1 rounded text-xs font-semibold ${origenColors[lead.origen] || 'bg-slate-200 text-slate-700'}`} data-testid={`lead-origen-${lead.id}`}>
                                    {lead.origen || '-'}
                                  </span>
                                  {lead.pantalla_origen && <span className="ml-1 text-[10px] text-slate-500">({lead.pantalla_origen})</span>}
                                </td>
                                <td className="py-3 px-3">
                                  <span className="text-xs font-semibold uppercase">{lead.tipo_cta || '-'}</span>
                                </td>
                                <td className="py-3 px-3">
                                  <p className="font-bold">{lead.nombre || '-'}</p>
                                  <p className="text-xs text-orange-700/80">{lead.email}</p>
                                  {lead.telefono && <p className="text-xs text-orange-700/60">{lead.telefono}</p>}
                                </td>
                                <td className="py-3 px-3">
                                  <p>{lead.empresa || '-'}</p>
                                  {lead.disponibilidad && <p className="text-xs text-slate-500 capitalize">{lead.disponibilidad}</p>}
                                </td>
                                <td className="py-3 px-3" data-testid={`lead-ahorro-${lead.id}`}>
                                  {ahorro !== undefined && ahorro !== null
                                    ? <span className="font-bold text-emerald-700">{Number(ahorro).toLocaleString('es-ES')} €</span>
                                    : <span className="text-slate-300 text-xs">—</span>}
                                </td>
                                <td className="py-3 px-3">
                                  <select
                                    value={lead.estado || 'nuevo'}
                                    onChange={(e) => handleUpdateLeadCAES(lead.id, { estado: e.target.value })}
                                    className={`px-2 py-1 rounded-lg text-xs font-bold ${
                                      lead.estado === 'nuevo' ? 'bg-green-500/20 text-green-300' :
                                      lead.estado === 'contactado' ? 'bg-blue-500/20 text-blue-700' :
                                      lead.estado === 'cualificado' ? 'bg-purple-500/20 text-purple-700' :
                                      lead.estado === 'propuesta' ? 'bg-orange-500/20 text-orange-700' :
                                      'bg-emerald-500/20 text-emerald-700'
                                    }`}
                                  >
                                    <option value="nuevo">Nuevo</option>
                                    <option value="contactado">Contactado</option>
                                    <option value="cualificado">Cualificado</option>
                                    <option value="propuesta">Propuesta</option>
                                    <option value="cerrado">Cerrado</option>
                                  </select>
                                </td>
                                <td className="py-3 px-3">
                                  <div className="flex gap-2">
                                    <a href={`mailto:${lead.email}`} className="p-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-blue-700 transition-all">
                                      <Mail className="w-4 h-4" />
                                    </a>
                                    {lead.telefono && (
                                      <a href={`tel:${lead.telefono}`} className="p-2 bg-green-500/20 hover:bg-green-500/30 rounded-lg text-green-300 transition-all">
                                        <Phone className="w-4 h-4" />
                                      </a>
                                    )}
                                    {lead.estado !== 'convertido' && !lead.cliente_id && (
                                      <button
                                        onClick={() => handleConvertirLead(lead)}
                                        title="Convertir a cliente"
                                        data-testid={`lead-convertir-${lead.id}`}
                                        className="p-2 bg-emerald-500/20 hover:bg-emerald-500/30 rounded-lg text-emerald-700 transition-all"
                                      >
                                        <UserPlus className="w-4 h-4" />
                                      </button>
                                    )}
                                    <button onClick={() => handleDeleteLeadCAES(lead.id)} className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-700 transition-all">
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* TAB: CITAS */}
            {tab === 'citas' && (
              <div className="bg-white shadow-sm border border-purple-500/20 rounded-2xl p-6">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-2xl font-black flex items-center gap-3">
                      <Calendar className="w-6 h-6 text-purple-400" /> Gestión de Citas
                    </h2>
                    <p className="text-purple-700/70 text-sm mt-1">Citas reservadas por clientes CAES</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCitasTab('lista')}
                      className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${citasTab === 'lista' ? 'bg-purple-500 text-white' : 'bg-purple-500/20 text-purple-700'}`}
                    >
                      Lista
                    </button>
                    <button
                      onClick={() => setCitasTab('calendario')}
                      className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${citasTab === 'calendario' ? 'bg-purple-500 text-white' : 'bg-purple-500/20 text-purple-700'}`}
                    >
                      Calendario
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => setCitasTab('config')}
                        className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${citasTab === 'config' ? 'bg-purple-500 text-white' : 'bg-purple-500/20 text-purple-700'}`}
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Sub-tab: Lista de citas */}
                {citasTab === 'lista' && (
                  <div>
                    {citas.length === 0 ? (
                      <div className="text-center py-12 text-purple-700/50">
                        <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No hay citas reservadas todavía</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-purple-500/20">
                              <th className="text-left py-3 px-3 font-bold text-purple-700">Fecha</th>
                              <th className="text-left py-3 px-3 font-bold text-purple-700">Hora</th>
                              <th className="text-left py-3 px-3 font-bold text-purple-700">Empresa</th>
                              <th className="text-left py-3 px-3 font-bold text-purple-700">Contacto</th>
                              <th className="text-left py-3 px-3 font-bold text-purple-700">CAES Est.</th>
                              <th className="text-left py-3 px-3 font-bold text-purple-700">Estado</th>
                              <th className="text-left py-3 px-3 font-bold text-purple-700">Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {citas.sort((a, b) => new Date(a.fecha + 'T' + a.hora) - new Date(b.fecha + 'T' + b.hora)).map((cita) => {
                              const fechaCita = new Date(cita.fecha);
                              const hoy = new Date();
                              hoy.setHours(0, 0, 0, 0);
                              const esPasada = fechaCita < hoy;
                              const esHoy = fechaCita.toDateString() === hoy.toDateString();
                              
                              return (
                                <tr key={cita.id} className={`border-b border-purple-500/10 hover:bg-purple-500/5 ${esPasada ? 'opacity-50' : ''}`}>
                                  <td className="py-3 px-3">
                                    <div className="flex items-center gap-2">
                                      {esHoy && <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">HOY</span>}
                                      <span className="font-semibold">
                                        {fechaCita.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="py-3 px-3 font-bold text-xl text-purple-700">{cita.hora}h</td>
                                  <td className="py-3 px-3">
                                    <div className="font-semibold">{cita.empresa}</div>
                                    <div className="text-xs text-purple-700/50">{cita.sector}</div>
                                  </td>
                                  <td className="py-3 px-3">
                                    <a href={`tel:${cita.telefono}`} className="flex items-center gap-1 text-purple-700 hover:text-slate-800 transition-all">
                                      <Phone className="w-3 h-3" /> {cita.telefono}
                                    </a>
                                    <div className="text-xs text-purple-700/50">{cita.email}</div>
                                  </td>
                                  <td className="py-3 px-3 font-bold text-orange-400">{cita.caesEstimado?.toLocaleString('es-ES')}€</td>
                                  <td className="py-3 px-3">
                                    <select
                                      value={cita.estado}
                                      onChange={async (e) => {
                                        try {
                                          await authAxios.put(`${API}/citas/${cita.id}`, { estado: e.target.value });
                                          setCitas(citas.map(c => c.id === cita.id ? { ...c, estado: e.target.value } : c));
                                          toast.success('Estado actualizado');
                                        } catch (err) {
                                          toast.error('Error actualizando estado');
                                        }
                                      }}
                                      className={`px-2 py-1 rounded-lg text-xs font-semibold ${
                                        cita.estado === 'pendiente' ? 'bg-yellow-500/20 text-yellow-300' :
                                        cita.estado === 'confirmada' ? 'bg-blue-500/20 text-blue-700' :
                                        cita.estado === 'completada' ? 'bg-green-500/20 text-green-300' :
                                        cita.estado === 'cancelada' ? 'bg-red-500/20 text-red-700' :
                                        'bg-gray-500/20 text-gray-300'
                                      }`}
                                    >
                                      <option value="pendiente">Pendiente</option>
                                      <option value="confirmada">Confirmada</option>
                                      <option value="completada">Completada</option>
                                      <option value="cancelada">Cancelada</option>
                                      <option value="no_show">No Show</option>
                                    </select>
                                  </td>
                                  <td className="py-3 px-3">
                                    <div className="flex gap-2">
                                      <a
                                        href={`tel:${cita.telefono}`}
                                        className="p-2 bg-green-500/20 hover:bg-green-500/30 rounded-lg text-green-300 transition-all"
                                        title="Llamar"
                                      >
                                        <Phone className="w-4 h-4" />
                                      </a>
                                      <button
                                        onClick={async () => {
                                          if (window.confirm('¿Eliminar esta cita?')) {
                                            try {
                                              await authAxios.delete(`${API}/citas/${cita.id}`);
                                              setCitas(citas.filter(c => c.id !== cita.id));
                                              toast.success('Cita eliminada');
                                            } catch (err) {
                                              toast.error('Error eliminando cita');
                                            }
                                          }
                                        }}
                                        className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-700 transition-all"
                                        title="Eliminar"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Sub-tab: Calendario */}
                {citasTab === 'calendario' && (
                  <div>
                    <div className="grid grid-cols-7 gap-2 mb-4">
                      {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(dia => (
                        <div key={dia} className="text-center text-purple-700/70 text-sm font-semibold py-2">{dia}</div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-2">
                      {(() => {
                        const hoy = new Date();
                        const inicioSemana = new Date(hoy);
                        inicioSemana.setDate(hoy.getDate() - hoy.getDay() + 1);
                        
                        const dias = [];
                        for (let i = 0; i < 28; i++) {
                          const fecha = new Date(inicioSemana);
                          fecha.setDate(inicioSemana.getDate() + i);
                          const fechaStr = fecha.toISOString().split('T')[0];
                          const citasDelDia = citas.filter(c => c.fecha === fechaStr && c.estado !== 'cancelada');
                          const esHoy = fecha.toDateString() === hoy.toDateString();
                          
                          dias.push(
                            <div
                              key={i}
                              className={`min-h-[80px] p-2 rounded-lg border ${
                                esHoy ? 'border-purple-400 bg-purple-500/10' : 'border-purple-500/20 bg-purple-500/5'
                              }`}
                            >
                              <div className={`text-sm font-semibold mb-1 ${esHoy ? 'text-purple-400' : 'text-purple-700/70'}`}>
                                {fecha.getDate()}
                              </div>
                              {citasDelDia.map(cita => (
                                <div
                                  key={cita.id}
                                  className="text-xs bg-orange-500/20 text-orange-700 px-1 py-0.5 rounded mb-1 truncate"
                                  title={`${cita.hora} - ${cita.empresa}`}
                                >
                                  {cita.hora} {cita.empresa.substring(0, 10)}
                                </div>
                              ))}
                            </div>
                          );
                        }
                        return dias;
                      })()}
                    </div>
                  </div>
                )}

                {/* Sub-tab: Configuración */}
                {citasTab === 'config' && isAdmin && configCitas && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-purple-700 mb-2">Duración de citas (minutos)</label>
                        <input
                          type="number"
                          value={configCitas.duracionMinutos}
                          onChange={(e) => setConfigCitas({ ...configCitas, duracionMinutos: parseInt(e.target.value) })}
                          className="w-full px-3 py-2 bg-purple-500/10 border border-purple-500/30 rounded-lg text-slate-800"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-purple-700 mb-2">Días de antelación</label>
                        <input
                          type="number"
                          value={configCitas.diasAntelacion}
                          onChange={(e) => setConfigCitas({ ...configCitas, diasAntelacion: parseInt(e.target.value) })}
                          className="w-full px-3 py-2 bg-purple-500/10 border border-purple-500/30 rounded-lg text-slate-800"
                        />
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold text-purple-700 mb-3">Horarios por día</h3>
                      <div className="space-y-2">
                        {['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'].map((diaNombre, idx) => {
                          const horario = configCitas.horarios?.find(h => h.dia === idx) || { dia: idx, activo: false, horaInicio: '09:00', horaFin: '14:00' };
                          return (
                            <div key={idx} className="flex items-center gap-4 bg-purple-500/5 p-3 rounded-lg">
                              <label className="flex items-center gap-2 w-28">
                                <input
                                  type="checkbox"
                                  checked={horario.activo}
                                  onChange={(e) => {
                                    const newHorarios = [...(configCitas.horarios || [])];
                                    const existingIdx = newHorarios.findIndex(h => h.dia === idx);
                                    if (existingIdx >= 0) {
                                      newHorarios[existingIdx] = { ...newHorarios[existingIdx], activo: e.target.checked };
                                    } else {
                                      newHorarios.push({ ...horario, activo: e.target.checked });
                                    }
                                    setConfigCitas({ ...configCitas, horarios: newHorarios });
                                  }}
                                  className="rounded"
                                />
                                <span className="text-sm font-semibold">{diaNombre}</span>
                              </label>
                              {horario.activo && (
                                <>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="time"
                                      value={horario.horaInicio}
                                      onChange={(e) => {
                                        const newHorarios = [...(configCitas.horarios || [])];
                                        const existingIdx = newHorarios.findIndex(h => h.dia === idx);
                                        if (existingIdx >= 0) {
                                          newHorarios[existingIdx] = { ...newHorarios[existingIdx], horaInicio: e.target.value };
                                        }
                                        setConfigCitas({ ...configCitas, horarios: newHorarios });
                                      }}
                                      className="px-2 py-1 bg-purple-500/10 border border-purple-500/30 rounded text-slate-800 text-sm"
                                    />
                                    <span className="text-purple-700/50">-</span>
                                    <input
                                      type="time"
                                      value={horario.horaFin}
                                      onChange={(e) => {
                                        const newHorarios = [...(configCitas.horarios || [])];
                                        const existingIdx = newHorarios.findIndex(h => h.dia === idx);
                                        if (existingIdx >= 0) {
                                          newHorarios[existingIdx] = { ...newHorarios[existingIdx], horaFin: e.target.value };
                                        }
                                        setConfigCitas({ ...configCitas, horarios: newHorarios });
                                      }}
                                      className="px-2 py-1 bg-purple-500/10 border border-purple-500/30 rounded text-slate-800 text-sm"
                                    />
                                  </div>
                                  <span className="text-purple-700/30">|</span>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="time"
                                      value={horario.horaInicio2 || ''}
                                      onChange={(e) => {
                                        const newHorarios = [...(configCitas.horarios || [])];
                                        const existingIdx = newHorarios.findIndex(h => h.dia === idx);
                                        if (existingIdx >= 0) {
                                          newHorarios[existingIdx] = { ...newHorarios[existingIdx], horaInicio2: e.target.value };
                                        }
                                        setConfigCitas({ ...configCitas, horarios: newHorarios });
                                      }}
                                      placeholder="Tarde"
                                      className="px-2 py-1 bg-purple-500/10 border border-purple-500/30 rounded text-slate-800 text-sm"
                                    />
                                    <span className="text-purple-700/50">-</span>
                                    <input
                                      type="time"
                                      value={horario.horaFin2 || ''}
                                      onChange={(e) => {
                                        const newHorarios = [...(configCitas.horarios || [])];
                                        const existingIdx = newHorarios.findIndex(h => h.dia === idx);
                                        if (existingIdx >= 0) {
                                          newHorarios[existingIdx] = { ...newHorarios[existingIdx], horaFin2: e.target.value };
                                        }
                                        setConfigCitas({ ...configCitas, horarios: newHorarios });
                                      }}
                                      className="px-2 py-1 bg-purple-500/10 border border-purple-500/30 rounded text-slate-800 text-sm"
                                    />
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <button
                      onClick={async () => {
                        setSavingConfigCitas(true);
                        try {
                          await authAxios.put(`${API}/config/citas`, configCitas);
                          toast.success('Configuración guardada');
                        } catch (err) {
                          toast.error('Error guardando configuración');
                        }
                        setSavingConfigCitas(false);
                      }}
                      disabled={savingConfigCitas}
                      className="px-6 py-3 bg-purple-500 hover:bg-purple-600 rounded-lg font-semibold flex items-center gap-2 transition-all disabled:opacity-50"
                    >
                      {savingConfigCitas ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Guardar Configuración
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* TAB: MENSAJES QUICK */}
            {tab === 'mensajes' && (
              <div className="bg-white shadow-sm border border-amber-500/20 rounded-2xl p-6">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-2xl font-black flex items-center gap-3"><MessageSquare className="w-6 h-6 text-amber-400" /> Mensajes Presentación Quick</h2>
                    <p className="text-amber-700/70 text-sm mt-1">Mensajes rotativos que aparecen en cada paso del análisis Quick</p>
                  </div>
                  {!editingMensajes && mensajesQuick && (
                    <button 
                      onClick={() => setEditingMensajes({
                        kpis: [...mensajesQuick.kpis],
                        vsSector: [...mensajesQuick.vsSector],
                        mejoras: [...mensajesQuick.mejoras]
                      })} 
                      className="px-4 py-2 bg-amber-500 hover:bg-amber-600 rounded-lg font-semibold flex items-center gap-2 transition-all"
                    >
                      <Edit2 className="w-4 h-4" /> Editar
                    </button>
                  )}
                  {editingMensajes && (
                    <div className="flex gap-2">
                      <button onClick={() => setEditingMensajes(null)} className="px-4 py-2 bg-gray-500 hover:bg-gray-600 rounded-lg font-semibold flex items-center gap-2 transition-all">
                        <X className="w-4 h-4" /> Cancelar
                      </button>
                      <button onClick={handleSaveMensajesQuick} disabled={savingMensajes} className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg font-semibold flex items-center gap-2 transition-all disabled:opacity-50">
                        {savingMensajes ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
                      </button>
                    </div>
                  )}
                </div>

                {mensajesQuick && (
                  <div className="space-y-8">
                    {/* KPIs */}
                    <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-5">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="font-bold text-lg flex items-center gap-2"><TrendingUp className="w-5 h-5 text-blue-400" /> Paso: KPIs</h3>
                        {editingMensajes && (
                          <button onClick={() => handleAddMensaje('kpis')} className="px-3 py-1 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-sm font-semibold flex items-center gap-1">
                            <Plus className="w-3 h-3" /> Añadir
                          </button>
                        )}
                      </div>
                      <div className="grid gap-3">
                        {(editingMensajes?.kpis || mensajesQuick.kpis).map((msg, idx) => (
                          <div key={idx} className="bg-white rounded-lg p-3 flex gap-3">
                            <div className="flex-1 space-y-2">
                              {editingMensajes ? (
                                <>
                                  <input value={msg.headline} onChange={(e) => handleUpdateMensaje('kpis', idx, 'headline', e.target.value)} className="w-full px-3 py-2 bg-white border border-blue-500/30 rounded-lg text-slate-800 font-bold text-sm" placeholder="Headline" />
                                  <input value={msg.subtext} onChange={(e) => handleUpdateMensaje('kpis', idx, 'subtext', e.target.value)} className="w-full px-3 py-2 bg-white border border-blue-500/30 rounded-lg text-blue-700 text-sm" placeholder="Subtext" />
                                </>
                              ) : (
                                <>
                                  <p className="font-bold text-slate-800">{msg.headline}</p>
                                  <p className="text-blue-700 text-sm">{msg.subtext}</p>
                                </>
                              )}
                            </div>
                            {editingMensajes && editingMensajes.kpis.length > 1 && (
                              <button onClick={() => handleRemoveMensaje('kpis', idx)} className="text-red-400 hover:text-red-700 p-1"><Trash2 className="w-4 h-4" /></button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* vs Sector */}
                    <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-5">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="font-bold text-lg flex items-center gap-2"><BarChart3 className="w-5 h-5 text-purple-400" /> Paso: vs Sector</h3>
                        {editingMensajes && (
                          <button onClick={() => handleAddMensaje('vsSector')} className="px-3 py-1 bg-purple-500/20 hover:bg-purple-500/30 rounded-lg text-sm font-semibold flex items-center gap-1">
                            <Plus className="w-3 h-3" /> Añadir
                          </button>
                        )}
                      </div>
                      <div className="grid gap-3">
                        {(editingMensajes?.vsSector || mensajesQuick.vsSector).map((msg, idx) => (
                          <div key={idx} className="bg-white rounded-lg p-3 flex gap-3">
                            <div className="flex-1 space-y-2">
                              {editingMensajes ? (
                                <>
                                  <input value={msg.headline} onChange={(e) => handleUpdateMensaje('vsSector', idx, 'headline', e.target.value)} className="w-full px-3 py-2 bg-white border border-purple-500/30 rounded-lg text-slate-800 font-bold text-sm" placeholder="Headline" />
                                  <input value={msg.subtext} onChange={(e) => handleUpdateMensaje('vsSector', idx, 'subtext', e.target.value)} className="w-full px-3 py-2 bg-white border border-purple-500/30 rounded-lg text-purple-700 text-sm" placeholder="Subtext" />
                                </>
                              ) : (
                                <>
                                  <p className="font-bold text-slate-800">{msg.headline}</p>
                                  <p className="text-purple-700 text-sm">{msg.subtext}</p>
                                </>
                              )}
                            </div>
                            {editingMensajes && editingMensajes.vsSector.length > 1 && (
                              <button onClick={() => handleRemoveMensaje('vsSector', idx)} className="text-red-400 hover:text-red-700 p-1"><Trash2 className="w-4 h-4" /></button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Mejoras */}
                    <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-5">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="font-bold text-lg flex items-center gap-2"><Target className="w-5 h-5 text-amber-400" /> Paso: Mejoras</h3>
                        {editingMensajes && (
                          <button onClick={() => handleAddMensaje('mejoras')} className="px-3 py-1 bg-amber-500/20 hover:bg-amber-500/30 rounded-lg text-sm font-semibold flex items-center gap-1">
                            <Plus className="w-3 h-3" /> Añadir
                          </button>
                        )}
                      </div>
                      <div className="grid gap-3">
                        {(editingMensajes?.mejoras || mensajesQuick.mejoras).map((msg, idx) => (
                          <div key={idx} className="bg-white rounded-lg p-3 flex gap-3">
                            <div className="flex-1 space-y-2">
                              {editingMensajes ? (
                                <>
                                  <input value={msg.headline} onChange={(e) => handleUpdateMensaje('mejoras', idx, 'headline', e.target.value)} className="w-full px-3 py-2 bg-white border border-amber-500/30 rounded-lg text-slate-800 font-bold text-sm" placeholder="Headline" />
                                  <input value={msg.subtext} onChange={(e) => handleUpdateMensaje('mejoras', idx, 'subtext', e.target.value)} className="w-full px-3 py-2 bg-white border border-amber-500/30 rounded-lg text-amber-700 text-sm" placeholder="Subtext" />
                                </>
                              ) : (
                                <>
                                  <p className="font-bold text-slate-800">{msg.headline}</p>
                                  <p className="text-amber-700 text-sm">{msg.subtext}</p>
                                </>
                              )}
                            </div>
                            {editingMensajes && editingMensajes.mejoras.length > 1 && (
                              <button onClick={() => handleRemoveMensaje('mejoras', idx)} className="text-red-400 hover:text-red-700 p-1"><Trash2 className="w-4 h-4" /></button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* TAB: HISTÓRICO */}
            {tab === 'historico' && (
              <div className="bg-white shadow-sm border border-slate-200 rounded-2xl p-6">
                <h2 className="text-2xl font-black flex items-center gap-3 mb-6"><History className="w-6 h-6 text-emerald-400" /> Histórico de Simulaciones</h2>
                {simulaciones.length === 0 ? (
                  <div className="text-center py-12 text-emerald-700"><History className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>No hay simulaciones guardadas</p></div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-3 font-bold text-emerald-700">Empresa</th>
                          <th className="text-left py-3 px-3 font-bold text-emerald-700">Estado</th>
                          <th className="text-left py-3 px-3 font-bold text-emerald-700">Sector</th>
                          <th className="text-center py-3 px-3 font-bold text-emerald-700">Flota</th>
                          <th className="text-center py-3 px-3 font-bold text-emerald-700">Piloto</th>
                          <th className="text-right py-3 px-3 font-bold text-emerald-700">Ahorro €</th>
                          <th className="text-left py-3 px-3 font-bold text-emerald-700">Comercial</th>
                          <th className="text-left py-3 px-3 font-bold text-emerald-700">Fecha</th>
                          <th className="text-center py-3 px-3 font-bold text-emerald-700">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {simulaciones.map((sim) => (
                          <tr key={sim.id} className="border-b border-slate-200 hover:bg-emerald-500/5">
                            <td className="py-3 px-3">
                              <div className="font-semibold">{sim.empresa}</div>
                              <div className="text-xs text-emerald-700">{sim.contacto}</div>
                            </td>
                            <td className="py-3 px-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-bold text-white ${estadoLabels[sim.estado]?.color || 'bg-gray-500'}`}>
                                {estadoLabels[sim.estado]?.label || sim.estado}
                              </span>
                            </td>
                            <td className="py-3 px-3 text-sm">{sectorNames[sim.sector] || sim.sector}</td>
                            <td className="py-3 px-3 text-center">
                              <span className="font-bold text-lg text-blue-400">{sim.numVehiculos || '-'}</span>
                              <span className="text-xs text-emerald-700 block">vehículos</span>
                            </td>
                            <td className="py-3 px-3 text-center">
                              <span className="font-bold text-lg text-amber-400">{sim.vehiculosPiloto || 5}</span>
                              <span className="text-xs text-emerald-700 block">piloto</span>
                            </td>
                            <td className="py-3 px-3 text-right font-bold text-emerald-400">{sim.totalAnnualSavingEuros?.toLocaleString('es-ES')} €</td>
                            <td className="py-3 px-3 text-sm text-emerald-700">{sim.comercialNombre || '-'}</td>
                            <td className="py-3 px-3 text-sm text-emerald-700">{new Date(sim.createdAt).toLocaleDateString('es-ES')}</td>
                            <td className="py-3 px-3">
                              <div className="flex justify-center gap-2">
                                <button onClick={() => handleViewSimulacion(sim.id)} className="p-2 hover:bg-emerald-500/20 rounded-lg transition-all" title="Ver" data-testid={`view-sim-${sim.id}`}><Eye className="w-4 h-4 text-emerald-400" /></button>
                                <button onClick={() => handleOpenPropuesta(sim.id)} className="p-2 hover:bg-purple-500/20 rounded-lg transition-all" title="Generar Propuesta" data-testid={`propuesta-sim-${sim.id}`}><FileText className="w-4 h-4 text-purple-400" /></button>
                                <button onClick={() => handleDeleteSimulacion(sim.id)} className="p-2 hover:bg-red-500/20 rounded-lg transition-all" title="Eliminar"><Trash2 className="w-4 h-4 text-red-400" /></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* TAB: CONFIGURACIÓN */}
            {tab === 'config' && editingConfig && (
              <div className="space-y-6">
                {/* Sub-tabs de configuración */}
                <div className="flex gap-2 flex-wrap bg-white p-2 rounded-xl">
                  <button onClick={() => setConfigSubTab('calculo')} className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${configSubTab === 'calculo' ? 'bg-emerald-500 text-white' : 'text-emerald-700 hover:bg-emerald-500/20'}`} data-testid="config-tab-calculo">
                    <Sliders className="w-4 h-4 inline mr-2" />Cálculo
                  </button>
                  <button onClick={() => setConfigSubTab('benchmarks')} className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${configSubTab === 'benchmarks' ? 'bg-emerald-500 text-white' : 'text-emerald-700 hover:bg-emerald-500/20'}`} data-testid="config-tab-benchmarks">
                    <BarChart3 className="w-4 h-4 inline mr-2" />Benchmarks
                  </button>
                  <button onClick={() => setConfigSubTab('piloto')} className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${configSubTab === 'piloto' ? 'bg-emerald-500 text-white' : 'text-emerald-700 hover:bg-emerald-500/20'}`} data-testid="config-tab-piloto">
                    <Truck className="w-4 h-4 inline mr-2" />Piloto
                  </button>
                  <button onClick={() => setConfigSubTab('precios')} className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${configSubTab === 'precios' ? 'bg-emerald-500 text-white' : 'text-emerald-700 hover:bg-emerald-500/20'}`} data-testid="config-tab-precios">
                    <Euro className="w-4 h-4 inline mr-2" />Precios Solución
                  </button>
                  <button onClick={() => setConfigSubTab('conocimiento')} className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${configSubTab === 'conocimiento' ? 'bg-emerald-500 text-white' : 'text-emerald-700 hover:bg-emerald-500/20'}`} data-testid="config-tab-conocimiento">
                    <Sparkles className="w-4 h-4 inline mr-2" />Base IA
                  </button>
                  <button onClick={() => setConfigSubTab('miteco')} className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${configSubTab === 'miteco' ? 'bg-blue-500 text-white' : 'text-blue-700 hover:bg-blue-500/20'}`} data-testid="config-tab-miteco">
                    <ExternalLink className="w-4 h-4 inline mr-2" />Datos MITECO
                  </button>
                </div>

                {/* Sub-tab: MITECO */}
                {configSubTab === 'miteco' && datosMiteco && (
                  <div className="bg-white shadow-sm border border-blue-500/20 rounded-2xl p-6">
                    <div className="flex justify-between items-start mb-6">
                      <div>
                        <h2 className="text-xl font-black flex items-center gap-3">
                          <ExternalLink className="w-5 h-5 text-blue-400" /> Datos MITECO (Certificados CAE)
                        </h2>
                        <p className="text-blue-700/70 text-sm mt-1">
                          Estos datos aparecen en el banner de credibilidad de la landing CAES
                        </p>
                      </div>
                      <button
                        onClick={async () => {
                          setSyncingMiteco(true);
                          try {
                            const response = await authAxios.post(`${API}/config/miteco/sync`);
                            if (response.data.success) {
                              setDatosMiteco(response.data.datos);
                              toast.success('Datos sincronizados con MITECO');
                            } else {
                              toast.error(response.data.error || 'Error al sincronizar');
                            }
                          } catch (error) {
                            toast.error('Error al sincronizar con MITECO');
                          }
                          setSyncingMiteco(false);
                        }}
                        disabled={syncingMiteco}
                        className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg font-semibold text-sm flex items-center gap-2 disabled:opacity-50 transition-all"
                      >
                        {syncingMiteco ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                        {syncingMiteco ? 'Sincronizando...' : 'Sincronizar con MITECO'}
                      </button>
                    </div>

                    {/* Estado de última sincronización */}
                    {datosMiteco.lastSyncAttempt && (
                      <div className={`mb-6 p-3 rounded-lg text-sm ${datosMiteco.lastSyncSuccess ? 'bg-green-500/10 border border-green-500/30' : 'bg-yellow-500/10 border border-yellow-500/30'}`}>
                        <p className={datosMiteco.lastSyncSuccess ? 'text-green-300' : 'text-yellow-300'}>
                          {datosMiteco.lastSyncSuccess ? '✓ Última sincronización exitosa' : '⚠ Última sincronización con problemas'}
                          {' - '}
                          {new Date(datosMiteco.lastSyncAttempt).toLocaleString('es-ES')}
                        </p>
                        {datosMiteco.lastSyncError && (
                          <p className="text-yellow-400/70 text-xs mt-1">{datosMiteco.lastSyncError}</p>
                        )}
                      </div>
                    )}

                    {/* Formulario editable */}
                    <div className="grid md:grid-cols-3 gap-6 mb-6">
                      <div>
                        <label className="block text-sm font-semibold text-blue-700 mb-2">GWh Total Certificado</label>
                        <input
                          type="number"
                          value={datosMiteco.gwhTotalCertificado || 0}
                          onChange={(e) => setDatosMiteco({ ...datosMiteco, gwhTotalCertificado: parseFloat(e.target.value) })}
                          className="w-full px-4 py-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-slate-800 text-lg font-bold"
                        />
                        <p className="text-blue-400/50 text-xs mt-1">Energía total certificada en España</p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-blue-700 mb-2">Actuaciones Totales</label>
                        <input
                          type="number"
                          value={datosMiteco.actuacionesTotales || 0}
                          onChange={(e) => setDatosMiteco({ ...datosMiteco, actuacionesTotales: parseInt(e.target.value) })}
                          className="w-full px-4 py-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-slate-800 text-lg font-bold"
                        />
                        <p className="text-blue-400/50 text-xs mt-1">Todas las actuaciones CAE</p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-emerald-700 mb-2">Actuaciones TRA-020</label>
                        <input
                          type="number"
                          value={datosMiteco.actuacionesTRA020 || 0}
                          onChange={(e) => setDatosMiteco({ ...datosMiteco, actuacionesTRA020: parseInt(e.target.value) })}
                          className="w-full px-4 py-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-slate-800 text-lg font-bold"
                        />
                        <p className="text-emerald-400/50 text-xs mt-1">Solo ficha transporte (la más rentable)</p>
                      </div>
                    </div>

                    {/* URL de fuente */}
                    <div className="mb-6">
                      <label className="block text-sm font-semibold text-blue-700 mb-2">URL de la fuente oficial</label>
                      <input
                        type="url"
                        value={datosMiteco.fuenteUrl || ''}
                        onChange={(e) => setDatosMiteco({ ...datosMiteco, fuenteUrl: e.target.value })}
                        className="w-full px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-lg text-blue-700 text-sm"
                      />
                    </div>

                    {/* Metadatos */}
                    <div className="flex items-center justify-between text-sm text-blue-700/50 mb-6">
                      <span>Última actualización: {datosMiteco.lastUpdated ? new Date(datosMiteco.lastUpdated).toLocaleString('es-ES') : 'N/A'}</span>
                      <span>Actualizado por: {datosMiteco.updatedBy === 'manual' ? '👤 Manual' : datosMiteco.updatedBy === 'auto-sync' ? '🤖 Auto-sync' : '⚙️ Sistema'}</span>
                    </div>

                    {/* Botón guardar */}
                    <button
                      onClick={async () => {
                        setSavingMiteco(true);
                        try {
                          await authAxios.put(`${API}/config/miteco`, {
                            gwhTotalCertificado: datosMiteco.gwhTotalCertificado,
                            actuacionesTotales: datosMiteco.actuacionesTotales,
                            actuacionesTRA020: datosMiteco.actuacionesTRA020,
                            fuenteUrl: datosMiteco.fuenteUrl
                          });
                          toast.success('Datos MITECO guardados');
                          // Recargar para obtener el timestamp actualizado
                          const response = await axios.get(`${API}/config/miteco`);
                          setDatosMiteco(response.data);
                        } catch (error) {
                          toast.error('Error guardando datos');
                        }
                        setSavingMiteco(false);
                      }}
                      disabled={savingMiteco}
                      className="px-6 py-3 bg-blue-500 hover:bg-blue-600 rounded-lg font-semibold flex items-center gap-2 disabled:opacity-50 transition-all"
                    >
                      {savingMiteco ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Guardar cambios manuales
                    </button>
                  </div>
                )}

                {/* Sub-tab: Cálculo */}
                {configSubTab === 'calculo' && (
                  <div className="bg-white shadow-sm border border-slate-200 rounded-2xl p-6">
                    <h2 className="text-xl font-black flex items-center gap-3 mb-6"><Sliders className="w-5 h-5 text-emerald-400" /> Constantes de Cálculo</h2>
                    <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {Object.entries(editingConfig.constants || {}).map(([key, value]) => (
                        <div key={key}>
                          <label className="block text-xs font-bold text-emerald-700 mb-1">{constantLabels[key] || key}</label>
                          <input
                            type="number"
                            step="0.01"
                            value={value}
                            onChange={(e) => setEditingConfig({
                              ...editingConfig,
                              constants: { ...editingConfig.constants, [key]: parseFloat(e.target.value) }
                            })}
                            className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-center"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sub-tab: Benchmarks */}
                {configSubTab === 'benchmarks' && (
                  <>
                    <div className="bg-white shadow-sm border border-slate-200 rounded-2xl p-6">
                      <h2 className="text-xl font-black flex items-center gap-3 mb-4"><BarChart3 className="w-5 h-5 text-emerald-400" /> Benchmarks por Sector</h2>
                      <div className="flex gap-2 mb-6 flex-wrap">
                        {Object.keys(editingConfig.benchmarks || {}).map((sector) => (
                          <button key={sector} onClick={() => setSelectedSector(sector)} className={`px-4 py-2 rounded-lg font-semibold transition-all ${selectedSector === sector ? 'bg-emerald-500 text-white' : 'bg-emerald-500/20 text-emerald-700'}`}>
                            {sectorNames[sector]}
                          </button>
                        ))}
                      </div>
                      {editingConfig.benchmarks?.[selectedSector] && (
                        <div className="space-y-4">
                          <div className="grid md:grid-cols-2 gap-4 mb-4">
                            <div>
                              <label className="block text-xs font-bold text-emerald-700 mb-1">Nombre del Sector</label>
                              <input type="text" value={editingConfig.benchmarks[selectedSector].name} onChange={(e) => setEditingConfig({...editingConfig, benchmarks: {...editingConfig.benchmarks, [selectedSector]: { ...editingConfig.benchmarks[selectedSector], name: e.target.value }}})} className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg" />
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-emerald-700 mb-1">Tamaño Muestra (flotas)</label>
                              <input type="number" value={editingConfig.benchmarks[selectedSector].sampleSize} onChange={(e) => setEditingConfig({...editingConfig, benchmarks: {...editingConfig.benchmarks, [selectedSector]: { ...editingConfig.benchmarks[selectedSector], sampleSize: parseInt(e.target.value) }}})} className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-center" />
                            </div>
                          </div>
                          {['consumo', 'accidentes', 'absentismo', 'multas', 'mantenimiento'].map((kpi) => (
                            <div key={kpi} className="bg-white rounded-xl p-4">
                              <h4 className="font-bold mb-3">{kpiLabels[kpi]}</h4>
                              <div className="grid grid-cols-4 gap-3">
                                {['media', 'p25', 'p75', 'p90'].map((stat) => (
                                  <div key={stat}>
                                    <label className="block text-xs text-emerald-400 mb-1">{stat === 'media' ? 'Media' : stat === 'p25' ? 'Top 25%' : stat === 'p75' ? 'Top 75%' : 'Bottom 10%'}</label>
                                    <input type="number" step="0.1" value={editingConfig.benchmarks[selectedSector][kpi]?.[stat] || 0} onChange={(e) => setEditingConfig({...editingConfig, benchmarks: {...editingConfig.benchmarks, [selectedSector]: {...editingConfig.benchmarks[selectedSector], [kpi]: { ...editingConfig.benchmarks[selectedSector][kpi], [stat]: parseFloat(e.target.value) }}}})} className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-center text-sm" />
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="bg-white shadow-sm border border-slate-200 rounded-2xl p-6">
                      <h2 className="text-xl font-black flex items-center gap-3 mb-4"><FileText className="w-5 h-5 text-emerald-400" /> Fuentes de los Benchmarks</h2>
                      <p className="text-emerald-700 text-sm mb-4">Esta información aparecerá en la página de comparativa y en los PDFs generados.</p>
                      <textarea value={editingConfig.benchmarkSources || ''} onChange={(e) => setEditingConfig({ ...editingConfig, benchmarkSources: e.target.value })} placeholder="Ej: Datos basados en estudio IDAE 2024..." rows={3} className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl resize-none" data-testid="benchmark-sources-input" />
                    </div>
                  </>
                )}

                {/* Sub-tab: Piloto */}
                {configSubTab === 'piloto' && (
                  <div className="space-y-6">
                    <div className="bg-white shadow-sm border border-slate-200 rounded-2xl p-6">
                      <h2 className="text-xl font-black flex items-center gap-3 mb-6"><Truck className="w-5 h-5 text-emerald-400" /> Configuración del Piloto</h2>
                      <div className="grid md:grid-cols-3 gap-4">
                        {Object.entries(editingConfig.piloto || {}).filter(([key]) => pilotoLabels[key]).map(([key, value]) => (
                          <div key={key}>
                            <label className="block text-xs font-bold text-emerald-700 mb-1">{pilotoLabels[key]}</label>
                            <input type="number" step="0.01" value={value} onChange={(e) => setEditingConfig({...editingConfig, piloto: { ...editingConfig.piloto, [key]: parseFloat(e.target.value) }})} className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-center" />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="bg-white shadow-sm border border-slate-200 rounded-2xl p-6">
                      <h2 className="text-xl font-black flex items-center gap-3 mb-4"><FileText className="w-5 h-5 text-emerald-400" /> Textos del Piloto</h2>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-bold text-emerald-700 mb-1">¿Qué incluye el piloto?</label>
                          <textarea value={editingConfig.piloto?.incluye_piloto || ''} onChange={(e) => setEditingConfig({ ...editingConfig, piloto: { ...editingConfig.piloto, incluye_piloto: e.target.value }})} rows={2} className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl resize-none" />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-emerald-700 mb-1">Texto de compromisos</label>
                          <textarea value={editingConfig.piloto?.texto_compromiso || ''} onChange={(e) => setEditingConfig({ ...editingConfig, piloto: { ...editingConfig.piloto, texto_compromiso: e.target.value }})} rows={2} className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl resize-none" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Sub-tab: Precios Solución */}
                {configSubTab === 'precios' && (
                  <div className="space-y-6">
                    {/* Nombres de Productos */}
                    <div className="bg-white shadow-sm border border-slate-200 rounded-2xl p-6">
                      <h2 className="text-xl font-black flex items-center gap-3 mb-6"><FileText className="w-5 h-5 text-emerald-400" /> Nombres de Productos</h2>
                      <p className="text-emerald-700 text-sm mb-4">Personaliza los nombres que aparecerán en las propuestas PDF</p>
                      <div className="grid md:grid-cols-3 gap-4 mb-4">
                        {['nombre_dispositivo_principal', 'nombre_iox_auxiliar', 'nombre_instalacion'].map((key) => (
                          <div key={key}>
                            <label className="block text-xs font-bold text-emerald-700 mb-1">{preciosLabels[key]}</label>
                            <input type="text" value={editingConfig.precios?.[key] || ''} onChange={(e) => setEditingConfig({...editingConfig, precios: { ...editingConfig.precios, [key]: e.target.value }})} className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg" />
                          </div>
                        ))}
                      </div>
                      <div className="grid md:grid-cols-3 gap-4">
                        {['nombre_saas_basico', 'nombre_saas_tacografo', 'nombre_saas_premium'].map((key) => (
                          <div key={key}>
                            <label className="block text-xs font-bold text-emerald-700 mb-1">{preciosLabels[key]}</label>
                            <input type="text" value={editingConfig.precios?.[key] || ''} onChange={(e) => setEditingConfig({...editingConfig, precios: { ...editingConfig.precios, [key]: e.target.value }})} className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg" />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="bg-white shadow-sm border border-slate-200 rounded-2xl p-6">
                      <h2 className="text-xl font-black flex items-center gap-3 mb-6"><Euro className="w-5 h-5 text-emerald-400" /> Precios Hardware</h2>
                      <div className="grid md:grid-cols-3 gap-4">
                        {['precio_dispositivo_go9', 'precio_iox_auxiliar', 'precio_instalacion'].map((key) => (
                          <div key={key}>
                            <label className="block text-xs font-bold text-emerald-700 mb-1">{preciosLabels[key]}</label>
                            <input type="number" step="0.01" value={editingConfig.precios?.[key] || 0} onChange={(e) => setEditingConfig({...editingConfig, precios: { ...editingConfig.precios, [key]: parseFloat(e.target.value) }})} className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-center" />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="bg-white shadow-sm border border-slate-200 rounded-2xl p-6">
                      <h2 className="text-xl font-black flex items-center gap-3 mb-6"><RefreshCw className="w-5 h-5 text-emerald-400" /> Precios SaaS (Mensual por vehículo)</h2>
                      <div className="grid md:grid-cols-3 gap-4">
                        {['saas_basico', 'saas_tacografo', 'saas_premium'].map((key) => (
                          <div key={key}>
                            <label className="block text-xs font-bold text-emerald-700 mb-1">{preciosLabels[key]}</label>
                            <input type="number" step="0.01" value={editingConfig.precios?.[key] || 0} onChange={(e) => setEditingConfig({...editingConfig, precios: { ...editingConfig.precios, [key]: parseFloat(e.target.value) }})} className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-center" />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="bg-white shadow-sm border border-slate-200 rounded-2xl p-6">
                      <h2 className="text-xl font-black flex items-center gap-3 mb-6"><FileText className="w-5 h-5 text-emerald-400" /> Condiciones</h2>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-emerald-700 mb-1">{preciosLabels['permanencia_minima_meses']}</label>
                          <input type="number" value={editingConfig.precios?.permanencia_minima_meses || 36} onChange={(e) => setEditingConfig({...editingConfig, precios: { ...editingConfig.precios, permanencia_minima_meses: parseInt(e.target.value) }})} className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-center" />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-emerald-700 mb-1">{preciosLabels['porcentaje_pago_hw_pedido']}</label>
                          <input type="number" step="0.1" value={editingConfig.precios?.porcentaje_pago_hw_pedido || 50} onChange={(e) => setEditingConfig({...editingConfig, precios: { ...editingConfig.precios, porcentaje_pago_hw_pedido: parseFloat(e.target.value) }})} className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-center" />
                        </div>
                      </div>
                      <div className="mt-4">
                        <label className="block text-xs font-bold text-emerald-700 mb-1">Texto descuento por volumen</label>
                        <input type="text" value={editingConfig.precios?.descuento_volumen_texto || ''} onChange={(e) => setEditingConfig({...editingConfig, precios: { ...editingConfig.precios, descuento_volumen_texto: e.target.value }})} className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg" />
                      </div>
                    </div>
                  </div>
                )}

                {/* Sub-tab: Base de Conocimiento IA */}
                {configSubTab === 'conocimiento' && (
                  <div className="bg-white shadow-sm border border-slate-200 rounded-2xl p-6">
                    <h2 className="text-xl font-black flex items-center gap-3 mb-2">
                      <Sparkles className="w-5 h-5 text-emerald-400" /> Base de Conocimiento IA
                    </h2>
                    <p className="text-emerald-700 text-sm mb-6">Define los problemas, soluciones y beneficios que la IA usará para generar diagnósticos personalizados.</p>
                    
                    <div className="space-y-6">
                      {[
                        { key: 'seguridad', name: 'Seguridad y Riesgo', icon: <Shield className="w-5 h-5" />, color: 'red' },
                        { key: 'combustible', name: 'Combustible', icon: <Fuel className="w-5 h-5" />, color: 'amber' },
                        { key: 'operativa', name: 'Operativa y Control', icon: <Gauge className="w-5 h-5" />, color: 'blue' },
                        { key: 'mantenimiento', name: 'Mantenimiento', icon: <Wrench className="w-5 h-5" />, color: 'emerald' },
                        { key: 'tic', name: 'TIC / Digitalización', icon: <Monitor className="w-5 h-5" />, color: 'purple' },
                        { key: 'sostenibilidad', name: 'Sostenibilidad / ESG', icon: <TreePine className="w-5 h-5" />, color: 'green' }
                      ].map(area => (
                        <div key={area.key} className={`bg-${area.color}-500/10 border border-${area.color}-500/20 rounded-xl p-4`}>
                          <h3 className={`font-bold text-${area.color}-400 mb-3 flex items-center gap-2`}>
                            {area.icon} {area.name}
                          </h3>
                          <div className="space-y-3">
                            {(editingConfig.baseConocimiento?.[area.key] || []).map((item, idx) => (
                              <div key={idx} className="bg-white rounded-lg p-3 space-y-3">
                                <div className="grid md:grid-cols-2 gap-3">
                                  <div>
                                    <label className="text-xs text-emerald-700 mb-1 block">Problema detectado</label>
                                    <input
                                      type="text"
                                      value={item.problema}
                                      onChange={(e) => {
                                        const newBase = { ...editingConfig.baseConocimiento };
                                        newBase[area.key][idx].problema = e.target.value;
                                        setEditingConfig({ ...editingConfig, baseConocimiento: newBase });
                                      }}
                                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-emerald-700 mb-1 block">Solución MOBILSAFE</label>
                                    <input
                                      type="text"
                                      value={item.solucion}
                                      onChange={(e) => {
                                        const newBase = { ...editingConfig.baseConocimiento };
                                        newBase[area.key][idx].solucion = e.target.value;
                                        setEditingConfig({ ...editingConfig, baseConocimiento: newBase });
                                      }}
                                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm"
                                    />
                                  </div>
                                </div>
                                <div className="flex gap-3 items-end">
                                  <div className="flex-1">
                                    <label className="text-xs text-emerald-700 mb-1 block">Beneficio para el cliente</label>
                                    <input
                                      type="text"
                                      value={item.beneficio || ''}
                                      onChange={(e) => {
                                        const newBase = { ...editingConfig.baseConocimiento };
                                        newBase[area.key][idx].beneficio = e.target.value;
                                        setEditingConfig({ ...editingConfig, baseConocimiento: newBase });
                                      }}
                                      placeholder="Ej: Reducción de costes, mejora eficiencia..."
                                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm placeholder-emerald-300/40"
                                    />
                                  </div>
                                  <button
                                    onClick={() => {
                                      const newBase = { ...editingConfig.baseConocimiento };
                                      newBase[area.key].splice(idx, 1);
                                      setEditingConfig({ ...editingConfig, baseConocimiento: newBase });
                                    }}
                                    className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                            <button
                              onClick={() => {
                                const newBase = { ...editingConfig.baseConocimiento };
                                if (!newBase[area.key]) newBase[area.key] = [];
                                newBase[area.key].push({ problema: '', solucion: '', beneficio: '' });
                                setEditingConfig({ ...editingConfig, baseConocimiento: newBase });
                              }}
                              className="w-full py-2 border-2 border-dashed border-emerald-500/30 rounded-lg text-emerald-400 hover:bg-emerald-500/10 flex items-center justify-center gap-2 text-sm"
                            >
                              <Plus className="w-4 h-4" /> Añadir problema-solución-beneficio
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Botón Guardar */}
                <div className="flex justify-end">
                  <button onClick={handleSaveConfig} disabled={savingConfig} className="px-8 py-3 bg-emerald-500 hover:bg-emerald-600 rounded-xl font-bold flex items-center gap-2 disabled:opacity-50" data-testid="save-config-btn">
                    {savingConfig ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                    Guardar Configuración
                  </button>
                </div>
              </div>
            )}

            {/* TAB: LANDINGS (Solo admins) */}
            {tab === 'landings' && isAdmin && (
              <div className="bg-white shadow-sm border border-orange-500/20 rounded-2xl p-6">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-2xl font-black flex items-center gap-3">
                      <FileText className="w-6 h-6 text-orange-400" /> Gestión de Contenido
                    </h2>
                    <p className="text-orange-700/70 text-sm mt-1">Edita textos y preguntas frecuentes sin tocar código</p>
                  </div>
                </div>

                {/* Sub-pestañas: Textos y FAQs */}
                <div className="flex gap-2 mb-6 border-b border-orange-500/20 pb-4">
                  <button
                    onClick={() => setLandingsSubTab('textos')}
                    className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                      landingsSubTab === 'textos' 
                        ? 'bg-orange-500 text-white' 
                        : 'bg-orange-500/20 text-orange-700 hover:bg-orange-500/30'
                    }`}
                    data-testid="subtab-textos"
                  >
                    <FileText className="w-4 h-4 inline mr-2" />
                    Textos de Landings
                  </button>
                  <button
                    onClick={() => setLandingsSubTab('faqs')}
                    className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                      landingsSubTab === 'faqs' 
                        ? 'bg-purple-500 text-white' 
                        : 'bg-purple-500/20 text-purple-700 hover:bg-purple-500/30'
                    }`}
                    data-testid="subtab-faqs"
                  >
                    <HelpCircle className="w-4 h-4 inline mr-2" />
                    FAQs CAES
                  </button>
                </div>

                {/* Sub-tab: TEXTOS DE LANDINGS */}
                {landingsSubTab === 'textos' && (
                  <>
                    {landingTextos.length === 0 ? (
                      <div className="text-center py-12 text-orange-700/70">
                        <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No hay landings configuradas</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {landingTextos.map((landing) => (
                          <div key={landing.id} className="border border-orange-500/20 rounded-xl overflow-hidden">
                            {/* Header del acordeón de landing */}
                            <button
                              onClick={() => {
                                if (expandedLanding === landing.id) {
                                  setExpandedLanding(null);
                                  setEditingLandingTextos(null);
                                  setExpandedSeccion(null);
                                } else {
                                  handleEditLandingTextos(landing);
                                }
                              }}
                              className={`w-full px-5 py-4 flex items-center justify-between text-left transition-all ${
                                expandedLanding === landing.id 
                                  ? 'bg-orange-500/20 border-b border-orange-500/20' 
                                  : 'bg-white hover:bg-orange-500/10'
                              }`}
                              data-testid={`landing-accordion-${landing.id}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                                  <FileText className="w-5 h-5 text-orange-400" />
                                </div>
                                <div>
                                  <h3 className="font-bold text-lg">{landing.nombre}</h3>
                                  <p className="text-xs text-orange-700/70">{landing.secciones?.length || 0} secciones editables</p>
                                </div>
                              </div>
                              <ChevronDown className={`w-5 h-5 text-orange-400 transition-transform ${expandedLanding === landing.id ? 'rotate-180' : ''}`} />
                            </button>

                        {/* Contenido expandido de la landing */}
                        {expandedLanding === landing.id && editingLandingTextos && (
                          <div className="p-5 space-y-4 bg-slate-50">
                            {/* Secciones como acordeones */}
                            {editingLandingTextos.secciones?.map((seccion) => (
                              <div key={seccion.id} className="border border-orange-500/10 rounded-lg overflow-hidden">
                                {/* Header de sección */}
                                <button
                                  onClick={() => setExpandedSeccion(expandedSeccion === seccion.id ? null : seccion.id)}
                                  className={`w-full px-4 py-3 flex items-center justify-between text-left transition-all ${
                                    expandedSeccion === seccion.id 
                                      ? 'bg-orange-500/10 border-b border-orange-500/10' 
                                      : 'bg-white hover:bg-orange-500/5'
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <ChevronRight className={`w-4 h-4 text-orange-400 transition-transform ${expandedSeccion === seccion.id ? 'rotate-90' : ''}`} />
                                    <span className="font-semibold">{seccion.nombre}</span>
                                    <span className="text-xs text-orange-700/50 ml-2">({Object.keys(seccion.campos || {}).length} campos)</span>
                                  </div>
                                </button>

                                {/* Campos de la sección */}
                                {expandedSeccion === seccion.id && (
                                  <div className="p-4 space-y-3 bg-white">
                                    {Object.entries(seccion.campos || {}).map(([campoKey, campoValue]) => (
                                      <div key={campoKey}>
                                        <label className="block text-xs font-bold text-orange-700 mb-1 capitalize">
                                          {campoKey.replace(/_/g, ' ')}
                                        </label>
                                        {campoValue && campoValue.length > 80 ? (
                                          <textarea
                                            value={campoValue}
                                            onChange={(e) => handleUpdateLandingCampo(seccion.id, campoKey, e.target.value)}
                                            rows={3}
                                            className="w-full px-3 py-2 bg-white border border-orange-500/30 rounded-lg text-sm resize-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400/30"
                                          />
                                        ) : (
                                          <input
                                            type="text"
                                            value={campoValue}
                                            onChange={(e) => handleUpdateLandingCampo(seccion.id, campoKey, e.target.value)}
                                            className="w-full px-3 py-2 bg-white border border-orange-500/30 rounded-lg text-sm focus:border-orange-400 focus:ring-1 focus:ring-orange-400/30"
                                          />
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}

                            {/* Botones de acción */}
                            <div className="flex justify-between items-center pt-4 border-t border-orange-500/20">
                              <button
                                onClick={() => handleResetLandingTextos(landing.id)}
                                className="px-4 py-2 text-red-400 hover:bg-red-500/10 rounded-lg text-sm font-semibold transition-all flex items-center gap-2"
                              >
                                <RefreshCw className="w-4 h-4" /> Restaurar originales
                              </button>
                              <div className="flex gap-2">
                                <button
                                  onClick={handleCancelEditLanding}
                                  className="px-4 py-2 bg-white hover:bg-slate-100 rounded-lg font-semibold text-sm"
                                >
                                  Cancelar
                                </button>
                                <button
                                  onClick={handleSaveLandingTextos}
                                  disabled={savingLandingTextos}
                                  className="px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg font-semibold text-sm flex items-center gap-2 disabled:opacity-50"
                                  data-testid={`save-landing-${landing.id}`}
                                >
                                  {savingLandingTextos ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                  Guardar cambios
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                  </>
                )}

                {/* Sub-tab: FAQs CAES */}
                {landingsSubTab === 'faqs' && (
                  <div className="space-y-4">
                    {faqsConfig.length === 0 ? (
                      <div className="text-center py-12 text-purple-700/70">
                        <HelpCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No hay FAQs configuradas</p>
                      </div>
                    ) : (
                      faqsConfig.map((faqConfig) => (
                        <div key={faqConfig.id} className="border border-purple-500/20 rounded-xl overflow-hidden">
                          {/* Header del acordeón de FAQs */}
                          <button
                            onClick={() => {
                              if (editingFaqs?.pagina === faqConfig.pagina) {
                                setEditingFaqs(null);
                              } else {
                                handleEditFaq(faqConfig);
                              }
                            }}
                            className={`w-full px-5 py-4 flex items-center justify-between text-left transition-all ${
                              editingFaqs?.pagina === faqConfig.pagina 
                                ? 'bg-purple-500/20 border-b border-purple-500/20' 
                                : 'bg-white hover:bg-purple-500/10'
                            }`}
                            data-testid={`faq-accordion-${faqConfig.pagina}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                                <HelpCircle className="w-5 h-5 text-purple-400" />
                              </div>
                              <div>
                                <h3 className="font-bold text-lg">{faqConfig.nombre}</h3>
                                <p className="text-xs text-purple-700/70">{faqConfig.faqs?.length || 0} preguntas frecuentes</p>
                              </div>
                            </div>
                            <ChevronDown className={`w-5 h-5 text-purple-400 transition-transform ${editingFaqs?.pagina === faqConfig.pagina ? 'rotate-180' : ''}`} />
                          </button>

                          {/* Editor de FAQs */}
                          {editingFaqs?.pagina === faqConfig.pagina && (
                            <div className="p-5 space-y-4 bg-slate-50">
                              {editingFaqs.faqs.sort((a, b) => a.orden - b.orden).map((faq, index) => (
                                <div key={faq.id} className={`border rounded-xl p-4 ${faq.activo ? 'border-purple-500/30 bg-purple-500/5' : 'border-slate-600/30 bg-slate-50 opacity-60'}`}>
                                  <div className="flex items-start justify-between gap-4 mb-3">
                                    <div className="flex items-center gap-2">
                                      <span className="bg-purple-500/30 text-purple-700 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm">
                                        {index + 1}
                                      </span>
                                      <div className="flex flex-col gap-1">
                                        <button
                                          onClick={() => handleMoveFaq(faq.id, 'up')}
                                          disabled={index === 0}
                                          className="p-1 hover:bg-purple-500/20 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                                          title="Subir"
                                        >
                                          <ChevronDown className="w-4 h-4 rotate-180" />
                                        </button>
                                        <button
                                          onClick={() => handleMoveFaq(faq.id, 'down')}
                                          disabled={index === editingFaqs.faqs.length - 1}
                                          className="p-1 hover:bg-purple-500/20 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                                          title="Bajar"
                                        >
                                          <ChevronDown className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <label className="flex items-center gap-2 text-sm text-slate-500 cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={faq.activo}
                                          onChange={(e) => handleUpdateFaqField(faq.id, 'activo', e.target.checked)}
                                          className="rounded border-purple-500"
                                        />
                                        Activo
                                      </label>
                                      <button
                                        onClick={() => handleDeleteFaq(faq.id)}
                                        className="p-2 hover:bg-red-500/20 rounded-lg text-red-400 transition-all"
                                        title="Eliminar FAQ"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>
                                  
                                  <div className="space-y-3 ml-10">
                                    <div>
                                      <label className="text-xs text-purple-700/70 mb-1 block">Pregunta</label>
                                      <input
                                        type="text"
                                        value={faq.pregunta}
                                        onChange={(e) => handleUpdateFaqField(faq.id, 'pregunta', e.target.value)}
                                        placeholder="Escribe la pregunta..."
                                        className="w-full px-4 py-2 bg-white border border-purple-500/30 rounded-lg text-slate-800 placeholder-slate-500"
                                        data-testid={`faq-pregunta-${faq.id}`}
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs text-purple-700/70 mb-1 block">Respuesta</label>
                                      <textarea
                                        value={faq.respuesta}
                                        onChange={(e) => handleUpdateFaqField(faq.id, 'respuesta', e.target.value)}
                                        placeholder="Escribe la respuesta..."
                                        rows={3}
                                        className="w-full px-4 py-2 bg-white border border-purple-500/30 rounded-lg text-slate-800 placeholder-slate-500 resize-y"
                                        data-testid={`faq-respuesta-${faq.id}`}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}

                              {/* Botón Añadir FAQ */}
                              <button
                                onClick={handleAddFaq}
                                className="w-full py-3 border-2 border-dashed border-purple-500/30 rounded-xl text-purple-400 hover:bg-purple-500/10 transition-all flex items-center justify-center gap-2"
                                data-testid="add-faq-btn"
                              >
                                <Plus className="w-5 h-5" />
                                Añadir nueva pregunta
                              </button>

                              {/* Botones de acción */}
                              <div className="flex justify-between pt-4 border-t border-purple-500/20">
                                <button
                                  onClick={() => handleResetFaqs(faqConfig.pagina)}
                                  className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-700 font-semibold text-sm flex items-center gap-2"
                                  data-testid={`reset-faqs-${faqConfig.pagina}`}
                                >
                                  <RefreshCw className="w-4 h-4" />
                                  Restaurar originales
                                </button>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => setEditingFaqs(null)}
                                    className="px-4 py-2 bg-white hover:bg-slate-100 rounded-lg font-semibold text-sm"
                                  >
                                    Cancelar
                                  </button>
                                  <button
                                    onClick={handleSaveFaqs}
                                    disabled={savingFaqs}
                                    className="px-4 py-2 bg-purple-500 hover:bg-purple-600 rounded-lg font-semibold text-sm flex items-center gap-2 disabled:opacity-50"
                                    data-testid={`save-faqs-${faqConfig.pagina}`}
                                  >
                                    {savingFaqs ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                    Guardar FAQs
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {/* TAB: USUARIOS (Solo admins) */}
            {tab === 'usuarios' && isAdmin && (
              <div className="bg-white shadow-sm border border-purple-500/20 rounded-2xl p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-black flex items-center gap-3">
                    <Shield className="w-6 h-6 text-purple-400" /> Gestión de Usuarios
                  </h2>
                  <button onClick={() => setShowAddUserForm(!showAddUserForm)} className="px-4 py-2 bg-purple-500 hover:bg-purple-600 rounded-lg font-semibold flex items-center gap-2 transition-all" data-testid="add-user-btn">
                    <Plus className="w-4 h-4" /> Nuevo Usuario
                  </button>
                </div>

                {showAddUserForm && (
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 mb-6">
                    <h3 className="font-bold text-purple-700 mb-4 flex items-center gap-2">
                      <Plus className="w-4 h-4" /> Crear Nuevo Usuario
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <input
                        type="text"
                        placeholder="Nombre completo"
                        value={newUser.nombre}
                        onChange={(e) => setNewUser({...newUser, nombre: e.target.value})}
                        className="px-4 py-2 bg-white border border-purple-500/30 rounded-lg"
                        data-testid="new-user-nombre"
                      />
                      <input
                        type="email"
                        placeholder="Email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                        className="px-4 py-2 bg-white border border-purple-500/30 rounded-lg"
                        data-testid="new-user-email"
                      />
                      <input
                        type="password"
                        placeholder="Contraseña"
                        value={newUser.password}
                        onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                        className="px-4 py-2 bg-white border border-purple-500/30 rounded-lg"
                        data-testid="new-user-password"
                      />
                      <select
                        value={newUser.role}
                        onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                        className="px-4 py-2 bg-white border border-purple-500/30 rounded-lg"
                        data-testid="new-user-role"
                      >
                        <option value="comercial">Comercial</option>
                        <option value="admin">Administrador</option>
                        <option value="superadmin">Super Admin</option>
                      </select>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={handleAddUser} className="px-4 py-2 bg-purple-500 hover:bg-purple-600 rounded-lg font-semibold flex items-center gap-2" data-testid="save-new-user-btn">
                        <Check className="w-4 h-4" /> Crear Usuario
                      </button>
                      <button onClick={() => { setShowAddUserForm(false); setNewUser({ nombre: '', email: '', password: '', role: 'comercial' }); }} className="px-4 py-2 bg-white hover:bg-slate-100 rounded-lg">
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-sm text-purple-700 border-b border-purple-500/20">
                        <th className="py-3 px-3">Usuario</th>
                        <th className="py-3 px-3">Email</th>
                        <th className="py-3 px-3">Rol</th>
                        <th className="py-3 px-3">Estado</th>
                        <th className="py-3 px-3">Creado</th>
                        <th className="py-3 px-3">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user.id} className="border-b border-purple-500/10 hover:bg-purple-500/10">
                          <td className="py-3 px-3">
                            {editingUser?.id === user.id ? (
                              <input
                                type="text"
                                value={editingUser.nombre}
                                onChange={(e) => setEditingUser({...editingUser, nombre: e.target.value})}
                                className="px-2 py-1 bg-white border border-purple-500/30 rounded-lg w-full"
                              />
                            ) : (
                              <div className="flex items-center gap-2">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${user.role === 'superadmin' ? 'bg-amber-500' : user.role === 'admin' ? 'bg-purple-500' : 'bg-emerald-500'}`}>
                                  {user.role === 'superadmin' || user.role === 'admin' ? <Shield className="w-4 h-4" /> : <User className="w-4 h-4" />}
                                </div>
                                <span className="font-bold">{user.nombre}</span>
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-3 text-sm">
                            {editingUser?.id === user.id ? (
                              <input
                                type="email"
                                value={editingUser.email}
                                onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                                className="px-2 py-1 bg-white border border-purple-500/30 rounded-lg w-full"
                              />
                            ) : (
                              user.email
                            )}
                          </td>
                          <td className="py-3 px-3">
                            {editingUser?.id === user.id ? (
                              <select
                                value={editingUser.role}
                                onChange={(e) => setEditingUser({...editingUser, role: e.target.value})}
                                className="px-2 py-1 bg-white border border-purple-500/30 rounded-lg"
                              >
                                <option value="comercial">Comercial</option>
                                <option value="admin">Administrador</option>
                                <option value="superadmin">Super Admin</option>
                              </select>
                            ) : (
                              <span className={`px-2 py-1 rounded-lg text-xs font-bold ${user.role === 'superadmin' ? 'bg-amber-500/20 text-amber-700' : user.role === 'admin' ? 'bg-purple-500/20 text-purple-700' : 'bg-emerald-500/20 text-emerald-700'}`}>
                                {user.role === 'superadmin' ? 'Super Admin' : user.role === 'admin' ? 'Admin' : 'Comercial'}
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-3">
                            {editingUser?.id === user.id ? (
                              <select
                                value={editingUser.activo ? 'activo' : 'inactivo'}
                                onChange={(e) => setEditingUser({...editingUser, activo: e.target.value === 'activo'})}
                                className="px-2 py-1 bg-white border border-purple-500/30 rounded-lg"
                              >
                                <option value="activo">Activo</option>
                                <option value="inactivo">Inactivo</option>
                              </select>
                            ) : (
                              <span className={`px-2 py-1 rounded-lg text-xs font-bold ${user.activo ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-700'}`}>
                                {user.activo ? 'Activo' : 'Inactivo'}
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-3 text-sm text-purple-700">
                            {new Date(user.createdAt).toLocaleDateString('es-ES')}
                          </td>
                          <td className="py-3 px-3">
                            <div className="flex gap-2">
                              {editingUser?.id === user.id ? (
                                <>
                                  <button onClick={() => handleUpdateUser(user.id, editingUser)} className="p-2 bg-green-500/20 hover:bg-green-500/30 rounded-lg text-green-300 transition-all">
                                    <Check className="w-4 h-4" />
                                  </button>
                                  <button onClick={() => setEditingUser(null)} className="p-2 bg-white hover:bg-slate-100 rounded-lg transition-all">
                                    <X className="w-4 h-4" />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button onClick={() => setEditingUser({...user})} className="p-2 bg-purple-500/20 hover:bg-purple-500/30 rounded-lg text-purple-700 transition-all" title="Editar">
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  {user.id !== currentUser.id && (
                                    <button onClick={() => handleDeleteUser(user.id)} className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-700 transition-all" title="Eliminar">
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {users.length === 0 && (
                  <div className="text-center py-10 text-purple-700">
                    <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No hay usuarios registrados</p>
                  </div>
                )}

                {/* Cambiar contraseña del usuario */}
                {editingUser && (
                  <div className="mt-6 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                    <h4 className="font-bold text-amber-700 mb-3 flex items-center gap-2">
                      <Key className="w-4 h-4" /> Cambiar Contraseña (opcional)
                    </h4>
                    <div className="flex gap-4">
                      <input
                        type="password"
                        placeholder="Nueva contraseña (dejar vacío para no cambiar)"
                        value={editingUser.password || ''}
                        onChange={(e) => setEditingUser({...editingUser, password: e.target.value})}
                        className="flex-1 px-4 py-2 bg-white border border-amber-500/30 rounded-lg"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* TAB: ANALYTICS */}
            {tab === 'analytics' && isAdmin && (
              <div className="bg-white shadow-sm border border-cyan-500/20 rounded-2xl p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-black flex items-center gap-3">
                    <BarChart3 className="w-6 h-6 text-cyan-400" /> Analytics de Visitas
                  </h2>
                  <button 
                    onClick={loadAnalytics} 
                    className="px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 rounded-lg font-semibold flex items-center gap-2 transition-all"
                    disabled={loadingAnalytics}
                  >
                    <RefreshCw className={`w-4 h-4 ${loadingAnalytics ? 'animate-spin' : ''}`} /> Actualizar
                  </button>
                </div>

                {/* Sub-pestañas de Analytics */}
                <div className="flex gap-2 mb-6">
                  <button 
                    onClick={() => setAnalyticsTab('overview')}
                    className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${analyticsTab === 'overview' ? 'bg-cyan-500 text-white' : 'bg-cyan-500/20 text-cyan-700 hover:bg-cyan-500/30'}`}
                  >
                    <TrendingUp className="w-4 h-4 inline mr-2" />Resumen
                  </button>
                  <button 
                    onClick={() => setAnalyticsTab('visitors')}
                    className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${analyticsTab === 'visitors' ? 'bg-cyan-500 text-white' : 'bg-cyan-500/20 text-cyan-700 hover:bg-cyan-500/30'}`}
                  >
                    <Users className="w-4 h-4 inline mr-2" />Visitantes
                  </button>
                  <button 
                    onClick={() => setAnalyticsTab('sessions')}
                    className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${analyticsTab === 'sessions' ? 'bg-cyan-500 text-white' : 'bg-cyan-500/20 text-cyan-700 hover:bg-cyan-500/30'}`}
                  >
                    <Clock className="w-4 h-4 inline mr-2" />Sesiones
                  </button>
                  <button
                    onClick={() => setAnalyticsTab('funnel')}
                    data-testid="analytics-subtab-funnel"
                    className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${analyticsTab === 'funnel' ? 'bg-cyan-500 text-white' : 'bg-cyan-500/20 text-cyan-700 hover:bg-cyan-500/30'}`}
                  >
                    <Target className="w-4 h-4 inline mr-2" />Funnel
                  </button>
                </div>

                {loadingAnalytics ? (
                  <div className="flex items-center justify-center py-20">
                    <RefreshCw className="w-8 h-8 text-cyan-400 animate-spin" />
                  </div>
                ) : (
                  <>
                    {/* Sub-tab: Resumen */}
                    {analyticsTab === 'overview' && analytics && (
                      <>
                        {/* Totales */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                          <div className="bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-xl p-4 text-center">
                            <p className="text-3xl font-black text-cyan-400">{analytics.totals.today}</p>
                            <p className="text-sm text-cyan-700/70">Hoy</p>
                          </div>
                          <div className="bg-gradient-to-br from-emerald-500/20 to-green-500/20 border border-emerald-500/30 rounded-xl p-4 text-center">
                            <p className="text-3xl font-black text-emerald-400">{analytics.totals.thisWeek}</p>
                            <p className="text-sm text-emerald-700/70">Esta semana</p>
                          </div>
                          <div className="bg-gradient-to-br from-purple-500/20 to-violet-500/20 border border-purple-500/30 rounded-xl p-4 text-center">
                            <p className="text-3xl font-black text-purple-400">{analytics.totals.thisMonth}</p>
                            <p className="text-sm text-purple-700/70">Este mes</p>
                          </div>
                          <div className="bg-gradient-to-br from-orange-500/20 to-red-500/20 border border-orange-500/30 rounded-xl p-4 text-center">
                            <p className="text-3xl font-black text-orange-400">{analytics.totals.all}</p>
                            <p className="text-sm text-orange-700/70">Total</p>
                          </div>
                        </div>

                        {/* Visitantes únicos */}
                        {visitors && (
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                            <div className="bg-gradient-to-br from-pink-500/20 to-rose-500/20 border border-pink-500/30 rounded-xl p-4 text-center">
                              <p className="text-3xl font-black text-pink-400">{visitors.totalUniqueVisitors}</p>
                              <p className="text-sm text-pink-700/70">Visitantes únicos</p>
                            </div>
                            <div className="bg-gradient-to-br from-amber-500/20 to-yellow-500/20 border border-amber-500/30 rounded-xl p-4 text-center">
                              <p className="text-3xl font-black text-amber-400">
                                {visitors.avgTimePerVisit ? `${Math.floor(visitors.avgTimePerVisit / 60)}:${String(visitors.avgTimePerVisit % 60).padStart(2, '0')}` : '0:00'}
                              </p>
                              <p className="text-sm text-amber-700/70">Tiempo medio</p>
                            </div>
                            <div className="bg-gradient-to-br from-teal-500/20 to-cyan-500/20 border border-teal-500/30 rounded-xl p-4 text-center">
                              <p className="text-3xl font-black text-teal-400">
                                {analytics.totals.all && visitors.totalUniqueVisitors ? (analytics.totals.all / visitors.totalUniqueVisitors).toFixed(1) : '0'}
                              </p>
                              <p className="text-sm text-teal-300/70">Visitas/usuario</p>
                            </div>
                          </div>
                        )}

                        {/* Gráfico de evolución */}
                        <div className="bg-slate-100 border border-cyan-500/20 rounded-xl p-6 mb-8">
                          <h3 className="font-bold text-cyan-700 mb-4 flex items-center gap-2">
                            <TrendingUp className="w-5 h-5" /> Evolución últimos 30 días
                          </h3>
                          <div className="h-48 flex items-end gap-1">
                            {analytics.dailyVisits.length > 0 ? (
                              analytics.dailyVisits.map((day, index) => {
                                const maxCount = Math.max(...analytics.dailyVisits.map(d => d.count));
                                const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                                return (
                                  <div 
                                    key={index} 
                                    className="flex-1 bg-gradient-to-t from-cyan-500 to-cyan-400 rounded-t hover:from-cyan-400 hover:to-cyan-300 transition-all cursor-pointer group relative"
                                    style={{ height: `${Math.max(height, 2)}%` }}
                                    title={`${day.date}: ${day.count} visitas`}
                                  >
                                    <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-slate-100 px-2 py-1 rounded text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                                      {day.date}: {day.count}
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="w-full text-center text-cyan-700/50 py-10">
                                No hay datos de visitas aún
                              </div>
                            )}
                          </div>
                          {analytics.dailyVisits.length > 0 && (
                            <div className="flex justify-between text-xs text-cyan-700/50 mt-2">
                              <span>{analytics.dailyVisits[0]?.date}</span>
                              <span>{analytics.dailyVisits[analytics.dailyVisits.length - 1]?.date}</span>
                            </div>
                          )}
                        </div>

                        {/* Páginas más visitadas y dispositivos */}
                        <div className="grid md:grid-cols-2 gap-6">
                          {/* Páginas más visitadas */}
                          <div className="bg-slate-100 border border-cyan-500/20 rounded-xl p-6">
                            <h3 className="font-bold text-cyan-700 mb-4 flex items-center gap-2">
                              <FileText className="w-5 h-5" /> Páginas más visitadas
                            </h3>
                            <div className="space-y-3">
                              {analytics.pageVisits.length > 0 ? (
                                analytics.pageVisits.map((page, index) => {
                                  const maxCount = analytics.pageVisits[0]?.count || 1;
                                  const width = (page.count / maxCount) * 100;
                                  return (
                                    <div key={index} className="relative">
                                      <div className="flex justify-between text-sm mb-1">
                                        <span className="text-cyan-100">{page.page || 'landing'}</span>
                                        <span className="text-cyan-400 font-bold">{page.count}</span>
                                      </div>
                                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                                        <div 
                                          className="h-full bg-gradient-to-r from-cyan-500 to-cyan-400 rounded-full"
                                          style={{ width: `${width}%` }}
                                        />
                                      </div>
                                    </div>
                                  );
                                })
                              ) : (
                                <p className="text-cyan-700/50 text-center py-4">Sin datos</p>
                              )}
                            </div>
                          </div>

                          {/* Dispositivos */}
                          <div className="bg-slate-100 border border-cyan-500/20 rounded-xl p-6">
                            <h3 className="font-bold text-cyan-700 mb-4 flex items-center gap-2">
                              <Monitor className="w-5 h-5" /> Dispositivos
                            </h3>
                            <div className="flex items-center justify-center gap-8 py-4">
                              <div className="text-center">
                                <div className="w-20 h-20 bg-cyan-500/20 rounded-xl flex items-center justify-center mx-auto mb-2">
                                  <Monitor className="w-10 h-10 text-cyan-400" />
                                </div>
                                <p className="text-2xl font-black text-cyan-400">{analytics.devices.desktop || 0}</p>
                                <p className="text-sm text-cyan-700/70">Desktop</p>
                              </div>
                              <div className="text-center">
                                <div className="w-20 h-20 bg-purple-500/20 rounded-xl flex items-center justify-center mx-auto mb-2">
                                  <Phone className="w-10 h-10 text-purple-400" />
                                </div>
                                <p className="text-2xl font-black text-purple-400">{analytics.devices.mobile || 0}</p>
                                <p className="text-sm text-purple-700/70">Móvil</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Sub-tab: Visitantes */}
                    {analyticsTab === 'visitors' && visitors && (
                      <div className="space-y-6">
                        <div className="bg-slate-100 border border-cyan-500/20 rounded-xl overflow-hidden">
                          <div className="p-4 border-b border-cyan-500/20">
                            <h3 className="font-bold text-cyan-700 flex items-center gap-2">
                              <Users className="w-5 h-5" /> Visitantes ({visitors.totalUniqueVisitors} únicos)
                            </h3>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-white">
                                <tr className="text-left text-cyan-700/70">
                                  <th className="px-4 py-3">ID</th>
                                  <th className="px-4 py-3">Visitas</th>
                                  <th className="px-4 py-3">Tiempo Total</th>
                                  <th className="px-4 py-3">Última Página</th>
                                  <th className="px-4 py-3">Dispositivo</th>
                                  <th className="px-4 py-3">Última Visita</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-cyan-500/10">
                                {visitors.visitors.map((visitor, index) => (
                                  <tr key={index} className="hover:bg-cyan-500/5 transition-colors">
                                    <td className="px-4 py-3">
                                      <span className="font-mono text-xs bg-cyan-500/20 px-2 py-1 rounded">
                                        {visitor.visitorId?.slice(-8) || 'N/A'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3">
                                      <span className="font-bold text-cyan-400">{visitor.totalVisits}</span>
                                    </td>
                                    <td className="px-4 py-3">
                                      <span className="text-emerald-400">
                                        {visitor.totalTime ? `${Math.floor(visitor.totalTime / 60)}:${String(visitor.totalTime % 60).padStart(2, '0')}` : '0:00'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3">
                                      <span className="text-cyan-100">{visitor.lastPage || '-'}</span>
                                    </td>
                                    <td className="px-4 py-3">
                                      {visitor.device === 'mobile' ? (
                                        <span className="flex items-center gap-1 text-purple-400">
                                          <Phone className="w-4 h-4" /> Móvil
                                        </span>
                                      ) : (
                                        <span className="flex items-center gap-1 text-cyan-400">
                                          <Monitor className="w-4 h-4" /> Desktop
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-cyan-700/70 text-xs">
                                      {visitor.lastVisit ? new Date(visitor.lastVisit).toLocaleString('es-ES') : '-'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Sub-tab: Sesiones */}
                    {analyticsTab === 'sessions' && sessions && (
                      <div className="space-y-6">
                        <div className="bg-slate-100 border border-cyan-500/20 rounded-xl overflow-hidden">
                          <div className="p-4 border-b border-cyan-500/20">
                            <h3 className="font-bold text-cyan-700 flex items-center gap-2">
                              <Clock className="w-5 h-5" /> Sesiones Recientes
                            </h3>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-white">
                                <tr className="text-left text-cyan-700/70">
                                  <th className="px-4 py-3">Fecha</th>
                                  <th className="px-4 py-3">Visitante</th>
                                  <th className="px-4 py-3">Página</th>
                                  <th className="px-4 py-3">Tiempo</th>
                                  <th className="px-4 py-3">Dispositivo</th>
                                  <th className="px-4 py-3"># Visita</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-cyan-500/10">
                                {sessions.sessions.map((session, index) => (
                                  <tr key={index} className="hover:bg-cyan-500/5 transition-colors">
                                    <td className="px-4 py-3 text-cyan-700/70 text-xs">
                                      {session.timestamp ? new Date(session.timestamp).toLocaleString('es-ES') : '-'}
                                    </td>
                                    <td className="px-4 py-3">
                                      <span className="font-mono text-xs bg-cyan-500/20 px-2 py-1 rounded">
                                        {session.visitorId?.slice(-8) || 'N/A'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3">
                                      <span className="text-cyan-100 font-medium">{session.page || '-'}</span>
                                    </td>
                                    <td className="px-4 py-3">
                                      <span className="text-emerald-400 font-bold">
                                        {session.timeOnPage ? `${Math.floor(session.timeOnPage / 60)}:${String(session.timeOnPage % 60).padStart(2, '0')}` : '-'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3">
                                      {session.device === 'mobile' ? (
                                        <Phone className="w-4 h-4 text-purple-400" />
                                      ) : (
                                        <Monitor className="w-4 h-4 text-cyan-400" />
                                      )}
                                    </td>
                                    <td className="px-4 py-3">
                                      <span className="bg-amber-500/20 text-amber-700 px-2 py-0.5 rounded text-xs">
                                        #{session.visitNumber || 1}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                    {!analytics && analyticsTab !== 'funnel' && (
                      <div className="text-center py-10 text-cyan-700">
                        <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No hay datos de analytics disponibles</p>
                      </div>
                    )}

                    {/* Sub-tab: Funnel (IMPL-055) */}
                    {analyticsTab === 'funnel' && (
                      <FunnelTab />
                    )}
                  </>
                )}
              </div>
            )}

            {/* TAB: REFLEXIONES */}
            {tab === 'reflexiones' && isAdmin && (
              <div className="bg-white shadow-sm border border-pink-500/20 rounded-2xl p-6">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-2xl font-black flex items-center gap-3">
                      <Sparkles className="w-6 h-6 text-pink-400" /> Reflexiones
                    </h2>
                    <p className="text-pink-700/70 text-sm mt-1">Ideas, pruebas y decisiones pendientes sobre la aplicación</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {reflexiones.map((reflexion) => (
                    <div key={reflexion.id} className="border border-pink-500/20 rounded-xl overflow-hidden">
                      {/* Header del acordeón */}
                      <button
                        onClick={() => setExpandedReflexion(expandedReflexion === reflexion.id ? null : reflexion.id)}
                        className={`w-full px-5 py-4 flex items-center justify-between text-left transition-all ${
                          expandedReflexion === reflexion.id 
                            ? 'bg-pink-500/20 border-b border-pink-500/20' 
                            : 'bg-white hover:bg-pink-500/10'
                        }`}
                        data-testid={`reflexion-${reflexion.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-pink-500/20 rounded-lg flex items-center justify-center">
                            <Sparkles className="w-5 h-5 text-pink-400" />
                          </div>
                          <div>
                            <h3 className="font-bold text-lg text-slate-800">{reflexion.titulo}</h3>
                            <p className="text-xs text-pink-700/70">Fecha: {reflexion.fecha}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            reflexion.estado === 'Pendiente de decisión' 
                              ? 'bg-amber-500/20 text-amber-700' 
                              : reflexion.estado === 'Implementado'
                              ? 'bg-emerald-500/20 text-emerald-700'
                              : 'bg-slate-500/20 text-slate-600'
                          }`}>
                            {reflexion.estado}
                          </span>
                          <ChevronDown className={`w-5 h-5 text-pink-400 transition-transform ${expandedReflexion === reflexion.id ? 'rotate-180' : ''}`} />
                        </div>
                      </button>

                      {/* Contenido expandido */}
                      {expandedReflexion === reflexion.id && (
                        <div className="p-5 bg-slate-50 space-y-4">
                          <div>
                            <h4 className="text-sm font-semibold text-pink-700 mb-2">Descripción:</h4>
                            <p className="text-slate-600">{reflexion.descripcion}</p>
                          </div>
                          
                          {reflexion.notas && (
                            <div>
                              <h4 className="text-sm font-semibold text-pink-700 mb-2">Notas:</h4>
                              <p className="text-slate-500 text-sm">{reflexion.notas}</p>
                            </div>
                          )}
                          
                          {reflexion.link && (
                            <div className="pt-4 border-t border-pink-500/20">
                              <a 
                                href={reflexion.link} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-pink-500/20 hover:bg-pink-500/30 text-pink-700 rounded-lg font-medium transition-all"
                              >
                                <ExternalLink className="w-4 h-4" />
                                {reflexion.linkTexto || 'Ver enlace'}
                              </a>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}

                  {reflexiones.length === 0 && (
                    <div className="text-center py-12 text-pink-700/70">
                      <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No hay reflexiones registradas</p>
                    </div>
                  )}
                </div>
              </div>
            )}


            {/* TAB: PROSPECCIÓN (IMPL-043) */}
            {/* TABS: PROSPECCIÓN CONFIG (IMPL-045) */}
            {tab === 'prospeccion-keywords' && isAdmin && (
              <ProspeccionPanel mode="keywords" />
            )}
            {tab === 'prospeccion-horario' && isAdmin && (
              <ProspeccionPanel mode="horario" />
            )}
            {tab === 'prospeccion-modelos' && isAdmin && (
              <ProspeccionPanel mode="modelos" />
            )}

            {/* TAB: CONFIG IA ACTAS (IMPL-047) */}
            {tab === 'config-ia-actas' && isAdmin && (
              <ConfigIAActas />
            )}


            {/* TAB: DOCUMENTACIÓN */}
            {tab === 'docs' && (
              <div className="bg-white shadow-sm border border-blue-500/20 rounded-2xl p-6">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-2xl font-black flex items-center gap-3">
                      <BookOpen className="w-6 h-6 text-blue-400" /> Documentación
                    </h2>
                    <p className="text-blue-700/70 text-sm mt-1">Manuales y guías de la aplicación</p>
                  </div>
                </div>
                
                {/* Documentos descargables */}
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  {/* Manual de Usuario */}
                  <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-2xl p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-blue-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                        <HelpCircle className="w-7 h-7 text-blue-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-slate-800 mb-2">Manual de Usuario</h3>
                        <p className="text-blue-700/70 text-sm mb-4">
                          Guía completa para usuarios de la aplicación. Incluye explicación de flujos, 
                          roles, panel de administración y consejos de uso.
                        </p>
                        <a 
                          href={`${API}/docs/manual-usuario`}
                          download="MOBILSAFE_Manual_Usuario.docx"
                          className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-500 hover:bg-blue-600 rounded-lg font-semibold text-sm transition-all"
                          data-testid="download-manual-usuario"
                        >
                          <Download className="w-4 h-4" /> Descargar Word
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Manual Técnico */}
                  <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-2xl p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-purple-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Settings className="w-7 h-7 text-purple-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-slate-800 mb-2">Manual Técnico</h3>
                        <p className="text-purple-700/70 text-sm mb-4">
                          Documentación técnica para desarrolladores. Arquitectura, API, base de datos, 
                          variables de entorno y dependencias.
                        </p>
                        <a 
                          href={`${API}/docs/manual-tecnico`}
                          download="MOBILSAFE_Manual_Tecnico.docx"
                          className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-500 hover:bg-purple-600 rounded-lg font-semibold text-sm transition-all"
                          data-testid="download-manual-tecnico"
                        >
                          <Download className="w-4 h-4" /> Descargar Word
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                {/* IMPL-051 · Ofertas sin cliente (refactor de IMPL-050) */}
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-5 mb-6" data-testid="docs-ofertas-sin-cliente">
                  <h4 className="font-bold text-orange-800 mb-3 flex items-center gap-2">
                    <BookOpen className="w-4 h-4" /> Ofertas sin cliente
                  </h4>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    Desde Desarrollo Comercial → "Oferta sin cliente" se pueden crear ofertas sin tener cliente real dado de alta
                    (típico para presentaciones rápidas, llamadas o exploración de números). Estas ofertas quedan en un historial
                    propio del comercial (módulo "Ofertas sin cliente"). Internamente cuelgan de un cliente técnico invisible
                    <code className="bg-orange-100 px-1 rounded text-xs">BorrNombreApellido</code>. Cuando el comercial decida que
                    la oferta corresponde a un cliente real, pulsa <strong>"Cargar en cliente →"</strong> y elige el cliente destino;
                    la oferta pasa a su ficha y desaparece del historial sin cliente. Los borradores no aparecen en la lista de
                    clientes ni se pueden buscar.
                  </p>
                </div>

                {/* IMPL-049 · Funcionamiento del cron emails */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-6" data-testid="docs-cron-emails">
                  <h4 className="font-bold text-emerald-800 mb-3 flex items-center gap-2">
                    <BookOpen className="w-4 h-4" /> Funcionamiento del cron emails
                  </h4>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    El cron lee el buzón <strong>imartinez@mobilsafe.es</strong> cada 2 horas. Filtra únicamente
                    emails de <code className="bg-emerald-100 px-1 rounded text-xs">noreply@notifications.hubspot.com</code>.
                    Los emails de tipo <strong>"mención"</strong> (Pablo Mir te mencionó en...) se procesan con IA
                    y generan acta tipo <strong>nota</strong> con resumen, valoración y tareas. Los emails de tipo
                    <strong> "correo incluido en registros"</strong> se guardan como registro simple sin IA. El cliente
                    se detecta por el nombre de empresa en el asunto. Si no se encuentra cliente, el acta queda
                    <strong> sin asignar</strong> para revisión manual.
                  </p>
                </div>

                {/* Contenido del Manual Técnico (índice) */}
                <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-5">
                  <h4 className="font-bold text-purple-700 mb-3 flex items-center gap-2">
                    <Settings className="w-4 h-4" /> Contenido del Manual Técnico
                  </h4>
                  <div className="grid md:grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                      <span className="w-6 h-6 bg-purple-500/30 rounded-full flex items-center justify-center text-xs font-bold">1</span>
                      Arquitectura del sistema
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <span className="w-6 h-6 bg-purple-500/30 rounded-full flex items-center justify-center text-xs font-bold">2</span>
                      Acceso al repositorio de código
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <span className="w-6 h-6 bg-purple-500/30 rounded-full flex items-center justify-center text-xs font-bold">3</span>
                      Documentación de instalación
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <span className="w-6 h-6 bg-purple-500/30 rounded-full flex items-center justify-center text-xs font-bold">4</span>
                      Esquema de base de datos
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <span className="w-6 h-6 bg-purple-500/30 rounded-full flex items-center justify-center text-xs font-bold">5</span>
                      Documentación de API
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <span className="w-6 h-6 bg-purple-500/30 rounded-full flex items-center justify-center text-xs font-bold">6</span>
                      Variables de entorno
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <span className="w-6 h-6 bg-purple-500/30 rounded-full flex items-center justify-center text-xs font-bold">7</span>
                      Dependencias externas
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* MODAL: Ver Simulación */}
      {(viewingSimulacion || loadingSimulacion) && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => !loadingSimulacion && setViewingSimulacion(null)}>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-emerald-500/30" onClick={(e) => e.stopPropagation()}>
            {loadingSimulacion ? (
              <div className="flex items-center justify-center py-20">
                <RefreshCw className="w-12 h-12 text-emerald-400 animate-spin" />
              </div>
            ) : viewingSimulacion && (
              <>
                {/* Header del Modal */}
                <div className="sticky top-0 bg-gradient-to-r from-emerald-600 to-green-600 px-6 py-4 flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-black text-slate-800">{viewingSimulacion.empresa}</h2>
                    <p className="text-emerald-100 text-sm">{sectorNames[viewingSimulacion.sector]} • {new Date(viewingSimulacion.createdAt).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </div>
                  <button onClick={() => setViewingSimulacion(null)} className="p-2 hover:bg-slate-100 rounded-lg transition-all" data-testid="close-modal-btn">
                    <X className="w-6 h-6 text-slate-800" />
                  </button>
                </div>

                <div className="p-6 space-y-6">
                  {/* Datos del Cliente */}
                  <div className="bg-white rounded-2xl p-5 border border-slate-200">
                    <h3 className="text-lg font-bold text-emerald-400 mb-4 flex items-center gap-2"><Building2 className="w-5 h-5" /> Datos del Cliente</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div><p className="text-emerald-700 text-xs mb-1">Empresa</p><p className="font-semibold">{viewingSimulacion.empresa}</p></div>
                      <div><p className="text-emerald-700 text-xs mb-1">Contacto</p><p className="font-semibold">{viewingSimulacion.contacto}</p></div>
                      <div><p className="text-emerald-700 text-xs mb-1">Email</p><p className="font-semibold">{viewingSimulacion.emailCliente || '-'}</p></div>
                      <div><p className="text-emerald-700 text-xs mb-1">Teléfono</p><p className="font-semibold">{viewingSimulacion.telefono || '-'}</p></div>
                      <div><p className="text-emerald-700 text-xs mb-1">Comercial</p><p className="font-semibold">{viewingSimulacion.comercialNombre || '-'}</p></div>
                      <div><p className="text-emerald-700 text-xs mb-1">Sector</p><p className="font-semibold">{sectorNames[viewingSimulacion.sector]}</p></div>
                    </div>
                  </div>

                  {/* Ahorro Principal */}
                  <div className="bg-gradient-to-r from-emerald-500 to-green-500 rounded-2xl p-6 text-center text-white">
                    <p className="text-sm opacity-80 mb-2">AHORRO TOTAL ANUAL</p>
                    <p className="text-5xl font-black">{viewingSimulacion.resultado?.totalAnnualSavingEuros?.toLocaleString('es-ES')} €</p>
                    <p className="text-lg mt-2 opacity-90">{viewingSimulacion.resultado?.savingPerVehicle?.toLocaleString('es-ES')} €/vehículo</p>
                  </div>

                  {/* Métricas Grid */}
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-white rounded-xl p-4 text-center border border-slate-200">
                      <p className="text-emerald-700 text-xs mb-1">Ahorro Operativo</p>
                      <p className="text-2xl font-bold text-emerald-400">{viewingSimulacion.resultado?.totalOperationalSavingEuros?.toLocaleString('es-ES')} €</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 text-center border border-slate-200">
                      <p className="text-emerald-700 text-xs mb-1">CAES Neto</p>
                      <p className="text-2xl font-bold text-emerald-400">{viewingSimulacion.resultado?.caesNeto?.toLocaleString('es-ES')} €</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 text-center border border-slate-200">
                      <p className="text-emerald-700 text-xs mb-1">CO₂ Evitado</p>
                      <p className="text-2xl font-bold text-green-400">{viewingSimulacion.resultado?.co2ReductionTons?.toLocaleString('es-ES')} ton</p>
                    </div>
                  </div>

                  {/* Desglose de Ahorros */}
                  <div className="bg-white rounded-2xl p-5 border border-slate-200">
                    <h3 className="text-lg font-bold text-emerald-400 mb-4 flex items-center gap-2"><Euro className="w-5 h-5" /> Desglose de Ahorros</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b border-slate-200">
                        <span className="flex items-center gap-2"><Fuel className="w-4 h-4 text-amber-400" /> Combustible</span>
                        <span className="font-bold text-emerald-400">{viewingSimulacion.resultado?.fuelSavingEuros?.toLocaleString('es-ES')} €</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-slate-200">
                        <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-400" /> Accidentes</span>
                        <span className="font-bold text-emerald-400">{viewingSimulacion.resultado?.accidentSavingEuros?.toLocaleString('es-ES')} €</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-slate-200">
                        <span className="flex items-center gap-2"><UserX className="w-4 h-4 text-blue-400" /> Bajas laborales</span>
                        <span className="font-bold text-emerald-400">{viewingSimulacion.resultado?.absenteeismSavingEuros?.toLocaleString('es-ES')} €</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-slate-200">
                        <span className="flex items-center gap-2"><Receipt className="w-4 h-4 text-orange-400" /> Multas</span>
                        <span className="font-bold text-emerald-400">{viewingSimulacion.resultado?.fineSavingEuros?.toLocaleString('es-ES')} €</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="flex items-center gap-2"><Wrench className="w-4 h-4 text-gray-400" /> Mantenimiento</span>
                        <span className="font-bold text-emerald-400">{viewingSimulacion.resultado?.maintenanceSavingEuros?.toLocaleString('es-ES')} €</span>
                      </div>
                    </div>
                  </div>

                  {/* Datos de Entrada */}
                  <div className="bg-white rounded-2xl p-5 border border-slate-200">
                    <h3 className="text-lg font-bold text-emerald-400 mb-4 flex items-center gap-2"><Truck className="w-5 h-5" /> Datos de la Flota</h3>
                    <div className="grid md:grid-cols-3 gap-4 text-sm">
                      <div><p className="text-emerald-700 text-xs">Vehículos</p><p className="font-semibold">{viewingSimulacion.input?.numVehicles}</p></div>
                      <div><p className="text-emerald-700 text-xs">Km/año por vehículo</p><p className="font-semibold">{viewingSimulacion.input?.kmPerVehicleYear?.toLocaleString('es-ES')}</p></div>
                      <div><p className="text-emerald-700 text-xs">Consumo (L/100km)</p><p className="font-semibold">{viewingSimulacion.input?.fuelConsumption}</p></div>
                      <div><p className="text-emerald-700 text-xs">Precio combustible</p><p className="font-semibold">{viewingSimulacion.input?.fuelCostPerLiter} €/L</p></div>
                      <div><p className="text-emerald-700 text-xs">Accidentes (%)</p><p className="font-semibold">{viewingSimulacion.input?.accidentHoursPercent}%</p></div>
                      <div><p className="text-emerald-700 text-xs">Absentismo (%)</p><p className="font-semibold">{viewingSimulacion.input?.absenteeismPercent}%</p></div>
                    </div>
                  </div>

                  {/* Impacto Equivalente */}
                  <div className="bg-gradient-to-r from-lime-500/20 to-green-500/20 rounded-2xl p-5 border border-lime-500/30">
                    <h3 className="text-lg font-bold text-lime-400 mb-4 flex items-center gap-2"><Leaf className="w-5 h-5" /> Equivalencias</h3>
                    <div className="grid md:grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-3xl font-black text-lime-400">{viewingSimulacion.resultado?.kmGratis?.toLocaleString('es-ES')}</p>
                        <p className="text-sm text-lime-300">Km sin pagar combustible</p>
                      </div>
                      <div>
                        <p className="text-3xl font-black text-lime-400">{viewingSimulacion.resultado?.vueltasMundo}</p>
                        <p className="text-sm text-lime-300">Vueltas al mundo</p>
                      </div>
                      <div>
                        <p className="text-3xl font-black text-lime-400">{viewingSimulacion.resultado?.litrosAhorrados?.toLocaleString('es-ES')}</p>
                        <p className="text-sm text-lime-300">Litros ahorrados</p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Editor de Propuesta - Se muestra como página completa */}
      {/* IMPL-074 lint fix: EditorPropuesta no importado - dead code (setEditingPropuesta nunca se llama) */}
      {false && editingPropuesta && viewingSimulacion && (
        <div className="fixed inset-0 z-50">
          {/* <EditorPropuesta 
            simulacion={viewingSimulacion} 
            config={config}
            onClose={() => { setEditingPropuesta(null); setViewingSimulacion(null); }}
            onGenerate={(data) => {
              toast.success('Propuesta lista para generar PDF');
              console.log('Datos de propuesta:', data);
              // TODO: Generar PDF con los datos
            }}
          /> */}
        </div>
      )}
    </div>
  );
};


// === Sistema de tabs unificado (Sprint A · IMPL-045) ===
function TabBtn({ active, onClick, testid, title, shortcut, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      title={title}
      className={`px-4 py-2.5 text-sm border-b-2 transition whitespace-nowrap flex items-center gap-1.5 ${
        active
          ? 'border-[#0a6640] text-[#0a6640] font-medium'
          : shortcut
            ? 'border-transparent text-slate-500 hover:text-slate-700 italic'
            : 'border-transparent text-slate-500 hover:text-slate-700'
      }`}
    >
      {children}
    </button>
  );
}

function Badge({ children }) {
  return (
    <span className="bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full text-xs font-semibold ml-0.5">
      {children}
    </span>
  );
}


export default AdminPanel;
